// The interactive page: parameters on the left, options across the top, the
// building in 3D with a storey picker, numbers and compliance on the right.
// The engine is the same pure code the tests run; here it runs in the
// browser on every change, so nothing goes to a server.
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { generate, type Generated, type Result } from "../domain/generate.ts";
import { area, distanceTo, grow, ringArea, union, type MultiPoly, type Ring } from "../domain/geom.ts";
import { editFlat } from "./editor.ts";

declare const __COMMIT__: string;
import type { Unit } from "../domain/layout.ts";
import { DEFAULT_PROGRAMME, type Programme, type Rules, type UnitType } from "../domain/rules.ts";
import { RETAIL_RULES, RETAIL_SITES, SAMPLE_RULES, SITES } from "../fixtures/sample.ts";
import * as R from "./retail.ts";
import { DEFAULT_RETAIL, type RetailProgramme } from "../domain/retail/programme.ts";
import { DEFAULT_RETAIL_FINANCE, type RetailFinanceInputs } from "../domain/retail/finance.ts";
import type { Sheet } from "../domain/export/xlsx.ts";
import { parseCoordinates, type AxisOrder } from "../domain/import/coords.ts";
import { parseDxf, type DxfOutline } from "../domain/import/dxf.ts";
import { edgeLength, longestEdge, preparePlot, suggestEdges, type PreparedPlot } from "../domain/plot.ts";
import type { EdgeKind, Site } from "../domain/rules.ts";
import type { Derogation } from "../rules/catuc.ts";
import { MIN_DWELLING_AREA } from "../rules/ro.ts";
import { SUN_HOURS } from "../domain/layout.ts";
import { latitudeFromStereo70 } from "../domain/sun.ts";
import { DEFAULT_FINANCE, proForma, residualLand, sensitivity, type FinanceInputs, type ProForma } from "../domain/finance.ts";
import { limits } from "../domain/indicators.ts";
import { optionDxf } from "../domain/export/dxf.ts";
import { flatsCsv, optionsCsv } from "../domain/export/csv.ts";
import { optionIfc } from "../domain/export/ifc.ts";
import { neighboursFrom, overpassQuery } from "../domain/context.ts";
import { workbook } from "../domain/export/xlsx.ts";
import { FURNITURE_NAME, furnish } from "../domain/furniture.ts";
import { ZONE, ZONE_FILL, ZONE_NAME, flatPlanSvg, flatSummary, roomChecks, roomNames } from "../domain/export/flatplan.ts";

// ---------- state ----------

type Mode = "residential" | "retail";
const startMode: Mode = (() => { try { return localStorage.getItem("da:mode") === "retail" ? "retail" : "residential"; } catch { return "residential"; } })();
const state = {
  /** Flats, or a retail park: the plot, the rules and the setbacks are shared, the programme is not. */
  mode: startMode as Mode,
  siteId: (startMode === "retail" ? RETAIL_SITES : SITES)[0].id as string, // a test plot's id, or "import"
  site: structuredClone((startMode === "retail" ? RETAIL_SITES : SITES)[0].site) as Site,
  imported: null as null | { name: string; plot: PreparedPlot; outlines?: DxfOutline[]; chosen?: number; chosenSet?: number[] },
  finance: structuredClone(DEFAULT_FINANCE) as FinanceInputs,
  sortBy: "sun" as "sun" | "profit" | "flats" | "gross" | "mix",
  targetMargin: 15,
  /** The street edge is still our guess (the longest edge); the first real choice replaces it. */
  streetGuessed: false,
  rules: structuredClone(startMode === "retail" ? RETAIL_RULES : SAMPLE_RULES) as Rules,
  prog: structuredClone(DEFAULT_PROGRAMME) as Programme,
  retail: structuredClone(DEFAULT_RETAIL) as RetailProgramme,
  retailFinance: structuredClone(DEFAULT_RETAIL_FINANCE) as RetailFinanceInputs,
  retailSort: "gla" as R.RetailSort,
  targetYoc: 8.5,
  optionKey: "",
  level: -100, // ALL: whole building; 0 ground; 1.. upper; -1.. basements
  view: "3d" as "3d" | "plan",
  envelope: false,
};
const ALL = -100;
const brand = load<{ name: string; logo?: string }>("da.brand", { name: "" });
{
  // the firm's own defaults, if it saved any
  const profile = load<{ mix?: Programme["mix"]; finance?: FinanceInputs; targetMargin?: number; groundUse?: Programme["groundUse"]; balconyDepth?: number } | null>("da.profile", null);
  if (profile) {
    if (profile.mix) state.prog.mix = profile.mix;
    if (profile.finance) state.finance = { ...DEFAULT_FINANCE, ...profile.finance };
    if (profile.targetMargin !== undefined) state.targetMargin = profile.targetMargin;
    if (profile.groundUse) state.prog.groundUse = profile.groundUse;
    if (profile.balconyDepth !== undefined) state.prog.balconyDepth = profile.balconyDepth;
    const rp = (profile as { retail?: { mix?: RetailProgramme["mix"]; finance?: RetailFinanceInputs } }).retail;
    if (rp?.mix) state.retail.mix = rp.mix;
    if (rp?.finance) state.retailFinance = { ...DEFAULT_RETAIL_FINANCE, ...rp.finance, rent: { ...DEFAULT_RETAIL_FINANCE.rent, ...rp.finance.rent } };
  }
}
let result: Result | null = null;
let current: Generated | null = null;

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T;
const fmt = (n: number, dp = 0) => n.toLocaleString("ro-RO", { maximumFractionDigits: dp, minimumFractionDigits: dp });
/** The test plots of the current mode, and any test plot's name by id. */
const samples = () => (state.mode === "retail" ? RETAIL_SITES : SITES);
const sampleName = (id: string) => [...SITES, ...RETAIL_SITES].find((x) => x.id === id)?.name;
const caseName = () => state.imported?.name ?? sampleName(state.siteId) ?? "Teren";

// ---------- colours (numeric for three.js, padded hex for CSS) ----------

const TYPE_COLOUR: Record<UnitType, number> = { "1": 0x8ecae6, "2": 0x3a86c8, "3": 0xf2b84b, "4": 0xe07a3f, "5": 0x9b6bd6 };
const KIND_COLOUR = { core: 0x4b5563, corridor: 0xf1e3c2, retail: 0xd9c9a3, parking: 0x9aa3af } as const;
const EDGE_COLOUR: Record<EdgeKind, number> = { aliniament: 0xd03b3b, lateral: 0x6b7280, posterior: 0x7c3aed };
const ROOM_TINT = { bath: 0xbfd7ea, hall: 0xe5e7eb, storage: 0xd1d5db, living: 0, bedroom: 0, kitchen: 0 } as const;
const ROOM_LABEL = { living: "zi", bedroom: "dorm.", kitchen: "buc.", bath: "baie", hall: "", storage: "" } as const;
const css = (n: number) => "#" + n.toString(16).padStart(6, "0");
const TYPE_NAME: Record<UnitType, string> = { "1": "Garsonieră", "2": "2 camere", "3": "3 camere", "4": "4 camere", "5": "5 camere" };
const TYPOLOGY_NAME = { bar: "Bară", L: "L", U: "U", perimeter: "Curte interioară", tower: "Turn" } as const;

// ---------- controls ----------

type Path = (s: typeof state) => [object, string];
interface Num { label: string; hint?: string; get: Path; min: number; max: number; step: number; unit?: string; show?: (v: number) => string }

const at = (obj: any, key: string): [object, string] => [obj, key];
const RULES: Num[] = [
  { label: "POT maxim", hint: "procent de ocupare a terenului", get: (s) => at(s.rules, "pot"), min: 10, max: 90, step: 1, unit: "%" },
  { label: "CUT maxim", hint: "coeficient de utilizare a terenului", get: (s) => at(s.rules, "cut"), min: 0.3, max: 6, step: 0.1 },
  { label: "Înălțime maximă", hint: "la cornișă", get: (s) => at(s.rules, "maxHeight"), min: 6, max: 60, step: 0.5, unit: "m" },
  { label: "Regim maxim", hint: "niveluri supraterane", get: (s) => at(s.rules, "maxStoreys"), min: 1, max: 20, step: 1, show: (v) => (v === 1 ? "P" : `P+${v - 1}E`) },
];
const SETBACKS: Num[] = [
  { label: "Față (aliniament)", get: (s) => at(s.rules.setbacks.aliniament, "min"), min: 0, max: 20, step: 0.5, unit: "m" },
  { label: "Laterale, minim", get: (s) => at(s.rules.setbacks.lateral, "min"), min: 0, max: 15, step: 0.5, unit: "m" },
  { label: "Posterioară, minim", get: (s) => at(s.rules.setbacks.posterior, "min"), min: 0, max: 20, step: 0.5, unit: "m" },
];
const PROGRAMME: Num[] = [
  { label: "Înălțime parter", get: (s) => at(s.prog, "groundStoreyHeight"), min: 2.8, max: 6, step: 0.1, unit: "m" },
  { label: "Înălțime etaj curent", get: (s) => at(s.prog, "storeyHeight"), min: 2.7, max: 4, step: 0.05, unit: "m" },
  { label: "Etaje retrase permise", get: (s) => at(s.prog, "setbackStoreys"), min: 0, max: 3, step: 1 },
  { label: "Adâncime balcon", hint: "0 = fără balcoane; intră în POT după Legea 169/2026", get: (s) => at(s.prog, "balconyDepth"), min: 0, max: 3, step: 0.1, unit: "m" },
  { label: "Lățime balcon", hint: "din frontul apartamentului", get: (s) => at(s.prog, "balconyShare"), min: 0.2, max: 1, step: 0.05, show: (v) => `${Math.round(v * 100)}%` },
  { label: "Eficiență (Au / Ad)", hint: "pentru estimări", get: (s) => at(s.prog, "efficiency"), min: 0.6, max: 0.9, step: 0.01 },
];
const PARKING: Num[] = [
  { label: "Locuri / apartament", hint: "din CU / PUZ sau norma locală", get: (s) => at(s.rules, "parkingPerDwelling"), min: 0, max: 3, step: 0.1 },
];

function numField(spec: Num): HTMLElement {
  const [obj, key] = spec.get(state);
  const v = (obj as any)[key] as number;
  const el = document.createElement("div");
  el.className = "field";
  el.innerHTML = `<label>${spec.label}${spec.hint ? `<small>${spec.hint}</small>` : ""}</label>
    <input type="number" min="${spec.min}" max="${spec.max}" step="${spec.step}" value="${v}">
    <input type="range" min="${spec.min}" max="${spec.max}" step="${spec.step}" value="${v}">`;
  const [num, range] = el.querySelectorAll("input");
  const lbl = el.querySelector("label")!;
  const paint = (x: number) => { if (spec.show) lbl.innerHTML = `${spec.label} · <b>${spec.show(x)}</b>${spec.hint ? `<small>${spec.hint}</small>` : ""}`; };
  paint(v);
  const set = (x: number, from: HTMLInputElement) => {
    if (!Number.isFinite(x)) return;
    const [o, k] = spec.get(state);
    (o as any)[k] = x;
    if (from !== num) num.value = String(x);
    if (from !== range) range.value = String(x);
    paint(x);
    schedule();
  };
  num.addEventListener("input", () => set(parseFloat(num.value), num));
  range.addEventListener("input", () => set(parseFloat(range.value), range));
  return el;
}

function seg<T extends string | number>(options: [T, string][], value: T, onPick: (v: T) => void): HTMLElement {
  const el = document.createElement("div");
  el.className = "seg";
  for (const [v, label] of options) {
    const b = document.createElement("button");
    b.textContent = label;
    b.className = v === value ? "on" : "";
    b.onclick = () => { el.querySelectorAll("button").forEach((x) => x.classList.remove("on")); b.classList.add("on"); onPick(v); };
    el.appendChild(b);
  }
  return el;
}

const RATIOS: [number, string][] = [[0, "fix"], [1 / 3, "H/3"], [0.5, "H/2"], [2 / 3, "2H/3"], [1, "H"]];

function stored(key: string): boolean | undefined {
  try { const v = localStorage.getItem(`da:${key}`); return v === null ? undefined : v === "1"; } catch { return undefined; }
}
function remember(key: string, on: boolean) {
  try { localStorage.setItem(`da:${key}`, on ? "1" : "0"); } catch { /* private window */ }
}

function buildControls() {
  const root = $("#controls");
  root.innerHTML = `<h1>Development Assistant</h1><div class="sub">${state.mode === "retail" ? "De la teren și reguli la masterplanul unui retail park" : "De la teren și reguli la clădire"} · versiunea ${__COMMIT__}</div>
    <div id="modePick" style="margin-top:8px"></div>
    <div class="notice"><b>Studiu preliminar, nu proiect.</b> <details class="more"><summary>Ce înseamnă</summary>Compartimentarea e schematică; regulile naționale marcate <span class="unv">neverificat</span> nu au fost citite în textul oficial;
    clădirile vecine intră în calcul doar dacă le încarci din OpenStreetMap. Datele terenului rămân în browserul tău: singura cerere care pleacă e cea pentru vecini, și o confirmi tu.</details></div>
    <div class="row" style="margin-top:8px">
      <button class="btn primary" id="explore" title="${state.mode === "retail" ? "Încearcă 24 de programe" : "Încearcă 216 programe"} și arată cele mai bune">Explorează automat</button>
      <button class="btn" id="report" title="O pagină de prezentat, tipărită sau PDF">Raport</button>
    </div>
    <details class="box"><summary>Salvează, deschide, trimite</summary>
      <div class="row" style="margin-top:6px">
        <button class="btn" id="saveCase">Salvează cazul (fișier)</button>
        <label class="btn">Deschide caz<input type="file" id="openCase" accept=".json" hidden></label>
      </div>
      <div class="row" style="margin-top:6px">
        <button class="btn" id="share">Copiază link</button>
        <button class="btn" id="printIt">Tipărește pagina</button>
      </div>
    </details>
    <details class="box"><summary>Proiectele mele</summary><div id="pipeline"></div><button class="btn" id="pipeSave" style="width:100%;margin-top:6px">Salvează terenul curent în listă</button></details>
    <details class="box"><summary>Profilul firmei</summary>
      <div class="field"><label>Numele firmei<small>pe raport</small></label><input type="text" id="brandName" value="${esc(brand.name)}"></div>
      <label class="btn" style="width:100%">Siglă (imagine)<input type="file" id="brandLogo" accept="image/*" hidden></label>
      ${brand.logo ? `<img src="${brand.logo}" alt="" style="max-height:40px;margin-top:6px">` : ""}
      <button class="btn" id="profileSave" style="width:100%;margin-top:6px">Folosește mixul, costurile și prețurile de acum ca implicite</button>
      <div class="sub">Rămân în acest browser. Nu le trimitem nicăieri.</div>
    </details>`;
  $("#modePick").appendChild(seg([["residential", "Locuințe"], ["retail", "Retail park"]] as [Mode, string][], state.mode, switchMode));
  // Numbered steps that fold: the panel is the workflow, in order, and a step
  // you are not working on is one line. Which ones are open is remembered.
  let box: HTMLElement = root;
  let step = 0;
  const section = (title: string, hint = "", open = false) => {
    const key = `step:${state.mode}:${title}`;
    const d = document.createElement("details");
    d.className = "step";
    d.open = stored(key) ?? open;
    d.innerHTML = `<summary><span class="n">${++step}</span>${title}${hint ? `<small>${hint}</small>` : ""}</summary>`;
    d.addEventListener("toggle", () => remember(key, d.open));
    root.appendChild(d);
    box = d;
  };

  section("Teren", "contur, laturi, vecini", true);
  const sel = document.createElement("select");
  sel.id = "sitepick";
  box.appendChild(sel);
  sel.onchange = () => {
    if (sel.value !== "import") {
      state.siteId = sel.value;
      state.site = structuredClone(samples().find((x) => x.id === sel.value)!.site);
    } else if (state.imported) {
      state.siteId = "import";
      state.site = { boundary: state.imported.plot.ring, edges: state.site.edges.length === state.imported.plot.ring.length ? state.site.edges : suggestEdges(state.imported.plot.ring, [longestEdge(state.imported.plot.ring)]) };
    }
    newPlot();
  };
  const imp = document.createElement("div");
  imp.innerHTML = `
    <div class="row" style="margin-top:8px">
      <label class="btn">Încarcă DXF<input type="file" id="dxf" accept=".dxf,.DXF" hidden></label>
      <button class="btn" id="coordsToggle">Lipește coordonate</button>
    </div>
    <div id="coordsBox" hidden>
      <textarea id="coords" rows="7" placeholder="Nr. pct.  X [m]  Y [m]&#10;1  325012.40  585310.22&#10;2  325018.95  585362.10&#10;..."></textarea>
      <div class="field"><label>Ordinea coloanelor<small>în inventarul de coordonate X = Nord, Y = Est</small></label><span></span></div>
      <div id="axisPick"></div>
      <button class="btn primary" id="coordsUse" style="width:100%;margin-top:8px">Folosește conturul</button>
    </div>
    <div id="importMsg" class="sub" style="margin-top:6px"></div>
    <button class="btn" id="osm" style="width:100%;margin-top:6px" hidden>Încarcă clădirile vecine (OpenStreetMap)</button>
    <div id="outlinePick"></div>
    <div id="plotInfo"></div>
    <div id="edgesBox"></div>
    <div class="sub" style="margin-top:6px"><a href="samples/teren-exemplu.dxf" download>exemplu DXF</a> · <a href="samples/inventar-exemplu.txt" download>exemplu inventar</a></div>`;
  box.appendChild(imp);
  let axis: AxisOrder = "north-east";
  $("#axisPick").appendChild(seg([["north-east", "X, Y (Nord, Est)"], ["east-north", "Est, Nord (CAD)"]] as [AxisOrder, string][], axis, (v) => (axis = v)));
  $("#coordsToggle").onclick = () => { $("#coordsBox").hidden = !$("#coordsBox").hidden; };
  $("#coordsUse").onclick = () => {
    const parsed = parseCoordinates(($("#coords") as HTMLTextAreaElement).value, axis);
    if (parsed.ring.length < 3) { message(parsed.warnings.join(" "), true); return; }
    useImported("Inventar de coordonate", parsed.ring, [...parsed.warnings, parsed.skipped.length ? (parsed.skipped.length === 1 ? "Un rând fără coordonate (antetul?) a fost ignorat." : `${parsed.skipped.length} rânduri fără coordonate au fost ignorate.`) : ""]);
  };
  ($("#dxf") as HTMLInputElement).onchange = async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try {
      const r = parseDxf(await file.text());
      if (r.outlines.length === 0) { message(`Niciun contur închis în ${file.name}${r.open ? ` (${r.open} polilinii deschise)` : ""}.`, true); return; }
      useImported(file.name, r.outlines[0].ring, [], r.outlines, 0);
    } catch (err) {
      message(`${file.name}: ${(err as Error).message}`, true);
    }
  };
  paintSitePicker();
  $("#osm").onclick = loadNeighbours;
  $("#saveCase").onclick = saveCase;
  ($("#openCase") as HTMLInputElement).onchange = (e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) openCase(f); };
  $("#printIt").onclick = () => window.print();
  $("#explore").onclick = () => (state.mode === "retail" ? R.explore($("#explore")) : explore());
  $("#report").onclick = () => (state.mode === "retail" ? R.report() : report());
  $("#share").onclick = shareLink;
  paintPipeline();
  $("#pipeSave").onclick = () => {
    const list = load<PipeItem[]>("da.cases", []);
    const best = result?.options[0];
    const rs = state.mode === "retail" ? R.summary() : null;
    list.unshift({ name: caseName(), savedAt: new Date().toISOString(), area: (state.mode === "retail" ? R.envelopeOf()?.plotArea : result?.envelope.plotArea) ?? 0,
      flats: state.mode === "retail" ? rs?.units ?? 0 : best?.dwellings.total ?? 0, profit: state.mode === "retail" ? rs?.profit ?? 0 : best ? pf(best).profit : 0,
      site: state.site, rules: state.rules, prog: state.prog, finance: state.finance, origin: state.imported?.plot.origin,
      mode: state.mode, retail: state.retail, retailFinance: state.retailFinance, gla: rs?.gla });
    save("da.cases", list.slice(0, 50));
    paintPipeline();
  };
  ($("#brandName") as HTMLInputElement).addEventListener("input", (e) => { brand.name = (e.target as HTMLInputElement).value; save("da.brand", brand); });
  ($("#brandLogo") as HTMLInputElement).onchange = async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    brand.logo = await new Promise<string>((res) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(file); });
    save("da.brand", brand);
    buildControls(); paintSitePicker(); paintEdges();
  };
  $("#profileSave").onclick = () => { save("da.profile", { mix: state.prog.mix, finance: state.finance, targetMargin: state.targetMargin, groundUse: state.prog.groundUse, balconyDepth: state.prog.balconyDepth, retail: { mix: state.retail.mix, finance: state.retailFinance } }); message("Profil salvat: proiectele noi pornesc cu valorile astea."); };

  section("Reguli urbanistice", "din CU / PUZ", true);
  RULES.forEach((s) => box.appendChild(numField(s)));

  const law = document.createElement("div");
  law.innerHTML = `
    <div class="field"><label>Regulamentul aprobat<small>definiția POT depinde de data aprobării</small></label><span></span></div>
    <div id="regimePick"></div>
    <div class="field"><label>Indicatorii vin din</label><select id="fromPick"><option value="PUG">PUG</option><option value="PUZ">PUZ</option><option value="PUD">PUD</option></select></div>
    <div class="field"><label>Simulează o derogare<small>Legea 169/2026, art. 66 și 82</small></label><span></span></div>
    <select id="derogPick">
      <option value="none">Fără derogare</option>
      <option value="pud">PUD: POT și CUT +20% (colț, calcan, front stradă cat. I–II)</option>
      <option value="puz">PUZ: POT și CUT +20%</option>
      <option value="puz-protected">PUZ în zonă protejată: +10%</option>
    </select>
    <div class="field" style="margin-top:10px"><label>Construcții existente păstrate<small>se adună la POT și CUT</small></label><span></span></div>
    <div class="row"><input type="number" id="exAc" min="0" step="10" placeholder="Ac m²" value="${state.rules.existing?.footprint ?? 0}"><input type="number" id="exAdc" min="0" step="10" placeholder="Adc m²" value="${state.rules.existing?.gross ?? 0}"></div>
    <div class="row sub"><span>amprentă (Ac)</span><span>desfășurată (Adc)</span></div>`;
  box.appendChild(law);
  $("#regimePick").appendChild(seg([["catuc", "după 25.08.2026"], ["legacy", "înainte (Legea 350)"]] as ["catuc" | "legacy", string][],
    state.rules.regime ?? "catuc", (v) => { state.rules.regime = v; schedule(); }));
  const from = $("#fromPick") as HTMLSelectElement;
  from.value = state.rules.indicatorsFrom ?? "PUZ";
  from.onchange = () => { state.rules.indicatorsFrom = from.value as "PUG" | "PUZ" | "PUD"; schedule(); };
  const derog = $("#derogPick") as HTMLSelectElement;
  derog.value = state.rules.derogation ?? "none";
  derog.onchange = () => { state.rules.derogation = derog.value as Derogation; schedule(); };
  const setExisting = () => {
    const ac = parseFloat(($("#exAc") as HTMLInputElement).value) || 0, adc = parseFloat(($("#exAdc") as HTMLInputElement).value) || 0;
    state.rules.existing = { footprint: ac, gross: adc };
    schedule();
  };
  $("#exAc").addEventListener("input", setExisting);
  $("#exAdc").addEventListener("input", setExisting);

  section("Retrageri", "față de limite");
  SETBACKS.forEach((s) => box.appendChild(numField(s)));
  for (const kind of ["lateral", "posterior"] as const) {
    const wrap = document.createElement("div");
    wrap.className = "field";
    wrap.innerHTML = `<label>${kind === "lateral" ? "Laterale" : "Posterioară"} în funcție de înălțime<small>max(minim, raport × H la cornișă)</small></label><span></span>`;
    const current = state.rules.setbacks[kind].heightRatio ?? 0;
    const pick = seg(RATIOS, RATIOS.reduce((b, r) => (Math.abs(r[0] - current) < Math.abs(b[0] - current) ? r : b))[0], (v) => { state.rules.setbacks[kind].heightRatio = v; schedule(); });
    pick.style.gridColumn = "1 / -1";
    wrap.appendChild(pick);
    box.appendChild(wrap);
  }

  if (state.mode === "retail") {
    // minGreen and the parking norm live in the retail steps; the rest of the residential programme does not apply
    R.steps((title, hint, open) => { section(title, hint ?? "", open ?? false); return box; });
    return;
  }

  section("Clădirea", "înălțimi, balcoane, adâncime");
  const use = document.createElement("div");
  use.className = "field";
  use.innerHTML = `<label>Parter</label><span></span>`;
  const useSeg = seg([["commercial", "Comercial"], ["residential", "Locuințe"]] as ["commercial" | "residential", string][], state.prog.groundUse, (v) => { state.prog.groundUse = v; schedule(); });
  useSeg.style.gridColumn = "1 / -1";
  use.appendChild(useSeg);
  box.appendChild(use);
  PROGRAMME.forEach((s) => box.appendChild(numField(s)));

  const depth = document.createElement("div");
  depth.innerHTML = `<div class="field"><label>Adâncime tronson<small>se încearcă de la max la min, din 2 în 2 m</small></label><span></span></div>
    <div class="row"><input type="number" id="dmin" min="9" max="24" step="1" value="${Math.min(...state.prog.depths)}"><input type="number" id="dmax" min="9" max="24" step="1" value="${Math.max(...state.prog.depths)}"></div>`;
  box.appendChild(depth);
  const setDepths = () => {
    const lo = Math.max(9, Math.min(+($("#dmin") as HTMLInputElement).value, +($("#dmax") as HTMLInputElement).value));
    const hi = Math.min(24, Math.max(+($("#dmin") as HTMLInputElement).value, +($("#dmax") as HTMLInputElement).value));
    const ds: number[] = [];
    for (let d = hi; d >= lo; d -= 2) ds.push(d);
    if (ds[ds.length - 1] !== lo) ds.push(lo);
    state.prog.depths = ds;
    schedule();
  };
  depth.querySelectorAll("input").forEach((i) => i.addEventListener("input", setDepths));

  section("Mix de apartamente", "tipuri, suprafețe, pe niveluri");
  const mix = document.createElement("table");
  mix.className = "mix";
  mix.innerHTML = `<tr><th>Tip</th><th>Pondere %</th><th>Suprafață utilă m²</th></tr>` + state.prog.mix.map((m, i) => `
    <tr><td><span class="swatch" style="background:${css(TYPE_COLOUR[m.type])}"></span>${TYPE_NAME[m.type]}</td>
    <td><input type="number" data-i="${i}" data-k="share" min="0" max="100" step="5" value="${Math.round(m.share * 100)}"></td>
    <td><input type="number" data-i="${i}" data-k="area" min="25" max="200" step="1" value="${m.area}"></td></tr>
    <tr><td></td><td colspan="2" class="warn" id="mixwarn${i}"></td></tr>`).join("");
  box.appendChild(mix);
  mix.querySelectorAll("input").forEach((inp) => inp.addEventListener("input", () => {
    const i = +inp.dataset.i!; const k = inp.dataset.k!; const v = parseFloat(inp.value);
    if (!Number.isFinite(v)) return;
    if (k === "share") state.prog.mix[i].share = v / 100; else state.prog.mix[i].area = v;
    paintMixWarnings();
    schedule();
  }));
  paintMixWarnings();

  // a different mix for a range of storeys
  const ranges = document.createElement("div");
  ranges.innerHTML = `<div class="sub" style="margin-top:8px">Mix diferit pe niveluri (de ex. apartamente mari la etajele retrase). Nivelul 0 = parter.</div><div id="rangeList"></div><button class="btn" id="rangeAdd" style="width:100%;margin-top:6px">Adaugă interval de niveluri</button>`;
  box.appendChild(ranges);
  const paintRanges = () => {
    const list = state.prog.mixByLevel ?? [];
    $("#rangeList").innerHTML = list.map((r, k) => `<div class="range" data-k="${k}">
      <div class="row"><label class="mini">de la<input type="number" min="0" max="30" data-f="from" value="${r.from}"></label><label class="mini">la<input type="number" min="0" max="30" data-f="to" value="${r.to}"></label></div>
      <div class="row4">${r.mix.map((m, i) => `<label class="mini">${m.type === "1" ? "G" : m.type + "C"} %<input type="number" min="0" max="100" step="5" data-i="${i}" value="${Math.round(m.share * 100)}"></label>`).join("")}</div>
      <button class="btn" data-del="${k}">Șterge intervalul</button></div>`).join("");
    $("#rangeList").querySelectorAll<HTMLElement>(".range").forEach((el) => {
      const r = list[+el.dataset.k!];
      el.querySelectorAll<HTMLInputElement>("input").forEach((inp) => inp.addEventListener("input", () => {
        const v = parseFloat(inp.value);
        if (!Number.isFinite(v)) return;
        if (inp.dataset.f) (r as any)[inp.dataset.f] = Math.round(v); else r.mix[+inp.dataset.i!].share = v / 100;
        schedule();
      }));
      el.querySelector<HTMLButtonElement>("[data-del]")!.onclick = () => { list.splice(+el.dataset.k!, 1); paintRanges(); schedule(); };
    });
  };
  $("#rangeAdd").onclick = () => {
    const top = Math.max(1, (current?.option.storeys.length ?? 8) - 1);
    (state.prog.mixByLevel ??= []).push({ from: Math.max(1, top - 1), to: top, mix: state.prog.mix.map((m) => ({ ...m, share: m.type >= "3" ? 0.5 : 0 })) });
    paintRanges();
    schedule();
  };
  paintRanges();

  section("Bani", "costuri și prețuri, ale tale");
  const fin = document.createElement("div");
  const fnum = (label: string, hint: string, get: () => number, set: (v: number) => void, step: number) => {
    const el = document.createElement("div");
    el.className = "field";
    el.innerHTML = `<label>${label}<small>${hint}</small></label><input type="number" step="${step}" value="${get()}">`;
    el.querySelector("input")!.addEventListener("input", (e) => { const v = parseFloat((e.target as HTMLInputElement).value); if (Number.isFinite(v)) { set(v); repaintMoney(); } });
    fin.appendChild(el);
  };
  const F = state.finance;
  fin.innerHTML = `<div class="sub">Valori orientative, nu date de piață: pune-le pe ale tale. Totul se recalculează pe loc.</div>`;
  fnum("Teren", "€, tot", () => F.land, (v) => (F.land = v), 10000);
  fnum("Construcție supraterană", "€ / m² Adc", () => F.costAbove, (v) => (F.costAbove = v), 10);
  fnum("Construcție subsol", "€ / m² de subsol", () => F.costBelow, (v) => (F.costBelow = v), 10);
  fnum("Costuri indirecte", "% din construcție: proiectare, avize, taxe", () => F.softPct, (v) => (F.softPct = v), 0.5);
  fnum("Neprevăzute", "% din construcție", () => F.contingencyPct, (v) => (F.contingencyPct = v), 0.5);
  for (const t of ["1", "2", "3", "4"] as UnitType[]) fnum(`Vânzare ${TYPE_NAME[t].toLowerCase()}`, "€ / m² util", () => F.salePerM2[t], (v) => (F.salePerM2[t] = v), 10);
  fnum("Vânzare comercial", "€ / m² construit", () => F.retailPerM2, (v) => (F.retailPerM2 = v), 10);
  fnum("Loc de parcare", "€ / loc", () => F.parkingSpace, (v) => (F.parkingSpace = v), 500);
  fnum("Marjă țintă", "% din venit, pentru valoarea reziduală a terenului", () => state.targetMargin, (v) => (state.targetMargin = v), 1);
  box.appendChild(fin);

  section("Parcare", "normă, sol, podium, subsol");
  PARKING.forEach((s) => box.appendChild(numField(s)));
  const pk = document.createElement("div");
  const P = state.prog.parking;
  pk.innerHTML = `
    <label class="check"><input type="checkbox" id="pkSurface" ${P.surface ? "checked" : ""}> La sol, pe terenul liber<small>cea mai ieftină; ia din spațiul verde</small></label>
    <div class="field"><label>Niveluri de podium<small>parcare supraterană în clădire; intră în CUT</small></label><input type="number" id="pkPodium" min="0" max="3" step="1" value="${P.podiumLevels}"></div>
    <label class="check"><input type="checkbox" id="pkUnder" ${P.underground ? "checked" : ""}> Subsol pentru ce nu încape<small>se deduce din CUT</small></label>
    <label class="check"><input type="checkbox" id="pkByType" ${state.rules.parkingByType ? "checked" : ""}> Normă diferită pe tip de apartament</label>
    <div id="pkTypes" ${state.rules.parkingByType ? "" : "hidden"} class="row4"></div>`;
  box.appendChild(pk);
  ($("#pkSurface") as HTMLInputElement).onchange = (e) => { P.surface = (e.target as HTMLInputElement).checked; schedule(); };
  ($("#pkUnder") as HTMLInputElement).onchange = (e) => { P.underground = (e.target as HTMLInputElement).checked; schedule(); };
  $("#pkPodium").addEventListener("input", (e) => { const v = parseInt((e.target as HTMLInputElement).value, 10); if (Number.isFinite(v)) { P.podiumLevels = Math.max(0, Math.min(3, v)); schedule(); } });
  const types = $("#pkTypes");
  const paintTypes = () => {
    const by = state.rules.parkingByType ?? {};
    types.innerHTML = (["1", "2", "3", "4"] as UnitType[]).map((t) => `<label class="mini">${t === "1" ? "G" : t + "C"}<input type="number" step="0.1" min="0" data-t="${t}" value="${by[t] ?? state.rules.parkingPerDwelling ?? 1}"></label>`).join("");
    types.querySelectorAll<HTMLInputElement>("input").forEach((i) => i.addEventListener("input", () => {
      const v = parseFloat(i.value);
      if (!Number.isFinite(v)) return;
      state.rules.parkingByType = { ...(state.rules.parkingByType ?? {}), [i.dataset.t!]: v };
      schedule();
    }));
  };
  ($("#pkByType") as HTMLInputElement).onchange = (e) => {
    const on = (e.target as HTMLInputElement).checked;
    types.hidden = !on;
    if (on) { const r = state.rules.parkingPerDwelling ?? 1; state.rules.parkingByType = { "1": r, "2": r, "3": r, "4": r }; paintTypes(); } else state.rules.parkingByType = undefined;
    schedule();
  };
  if (state.rules.parkingByType) paintTypes();
}

/**
 * Flats or a retail park. An imported plot and its rules stay; a test plot
 * becomes the first test plot of the other mode, with that mode's rules.
 */
function switchMode(mode: Mode) {
  if (mode === state.mode) return;
  state.mode = mode;
  try { localStorage.setItem("da:mode", mode); } catch { /* private window */ }
  if (state.siteId !== "import") {
    state.siteId = samples()[0].id;
    state.site = structuredClone(samples()[0].site);
    state.rules = structuredClone(mode === "retail" ? RETAIL_RULES : SAMPLE_RULES);
  }
  result = null; current = null; R.clear();
  state.optionKey = "";
  buildControls(); paintSitePicker();
  newPlot();
}

/**
 * A case is everything needed to reproduce what someone saw: the plot, its
 * edges, the rules and the programme. Saved as a file so a tester can send it
 * back with their comment, and opened the same way.
 */
function saveCase() {
  const data = { version: 1, commit: __COMMIT__, savedAt: new Date().toISOString(), name: caseName(), mode: state.mode, site: state.site, rules: state.rules, prog: state.prog, retail: state.retail, retailFinance: state.retailFinance };
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  a.download = `caz-${(data.name ?? "teren").replace(/[^\p{L}\p{N}]+/gu, "-").toLowerCase()}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

async function openCase(file: File) {
  try {
    const data = JSON.parse(await file.text());
    if (!data.site?.boundary || !data.rules || !data.prog) throw new Error("fișierul nu e un caz salvat din aplicație");
    state.rules = data.rules;
    state.prog = { ...DEFAULT_PROGRAMME, ...data.prog };
    restoreRetail(data);
    const ring = data.site.boundary as Ring;
    state.imported = { name: data.name ?? file.name, plot: { ring, origin: [0, 0], area: Math.abs(ringArea(ring)), removed: 0, warnings: [] } };
    state.siteId = "import";
    state.site = data.site;
    state.streetGuessed = false;
    buildControls();
    paintSitePicker();
    newPlot();
    message(`Caz deschis: ${state.imported.name}${data.commit && data.commit !== __COMMIT__ ? ` (salvat cu versiunea ${data.commit})` : ""}.`);
  } catch (err) {
    message(`${file.name}: ${(err as Error).message}`, true);
  }
}

/** The mode and retail programme of a saved case, link or project; a case from before retail parks is residential. */
function restoreRetail(data: { mode?: Mode; retail?: Partial<RetailProgramme>; retailFinance?: Partial<RetailFinanceInputs> }) {
  state.mode = data.mode === "retail" ? "retail" : "residential";
  state.retail = { ...DEFAULT_RETAIL, ...(data.retail ?? {}) };
  state.retailFinance = { ...DEFAULT_RETAIL_FINANCE, ...(data.retailFinance ?? {}), rent: { ...DEFAULT_RETAIL_FINANCE.rent, ...(data.retailFinance?.rent ?? {}) } };
  result = null; current = null; R.clear();
}

function message(text: string, bad = false) {
  const el = $("#importMsg");
  el.textContent = text;
  el.style.color = bad ? "var(--bad)" : "";
}

function paintSitePicker() {
  const sel = $("#sitepick") as HTMLSelectElement;
  sel.innerHTML = samples().map((s) => `<option value="${s.id}">${s.name}</option>`).join("") +
    (state.imported ? `<option value="import">Importat: ${state.imported.name}</option>` : "");
  sel.value = state.siteId;
}

function useImported(name: string, raw: Ring, warnings: string[], outlines?: DxfOutline[], chosen?: number) {
  const plot = preparePlot(raw);
  state.imported = { name, plot, outlines, chosen };
  state.siteId = "import";
  // a Stereo 70 northing gives the latitude the sun needs; anything else keeps the default
  const north = plot.origin[1];
  state.site = { boundary: plot.ring, edges: suggestEdges(plot.ring, [longestEdge(plot.ring)]), latitude: north > 150_000 && north < 850_000 ? latitudeFromStereo70(north) : undefined };
  state.streetGuessed = true;
  message([`Contur cu ${plot.ring.length} laturi${plot.removed ? ` (${plot.removed} puncte coliniare eliminate)` : ""}.`,
    `Am presupus că latura la stradă e cea mai lungă (L${longestEdge(plot.ring) + 1}): marchează mai jos latura reală.`,
    ...plot.warnings, ...warnings].filter(Boolean).join(" "), plot.warnings.length > 0);
  paintSitePicker();
  newPlot();
}

function newPlot() {
  state.optionKey = "";
  state.level = ALL;
  paintOutlinePick();
  paintPlotInfo();
  paintEdges();
  frameCamera();
  schedule();
}

function paintOutlinePick() {
  const root = $("#outlinePick");
  const imp = state.imported;
  if (state.siteId !== "import" || !imp?.outlines || imp.outlines.length < 2) { root.innerHTML = ""; return; }
  const chosen = imp.chosenSet ?? [imp.chosen ?? 0];
  root.innerHTML = `<h2>Contururi în fișier</h2><div class="sub">Am ales cel mai mare. Bifează parcela; bifează mai multe ca să le comasezi.</div>` +
    imp.outlines.slice(0, 12).map((o, i) => `<label class="pick"><input type="checkbox" value="${i}" ${chosen.includes(i) ? "checked" : ""}>
      <span>${fmt(o.area)} m²</span><span class="src">strat ${o.layer} · ${o.ring.length} puncte</span></label>`).join("");
  root.querySelectorAll<HTMLInputElement>("input").forEach((r) => (r.onchange = () => {
    const picked = [...root.querySelectorAll<HTMLInputElement>("input:checked")].map((x) => +x.value);
    if (!picked.length) return;
    // assemblage: adjoining parcels merged into one plot; a gap between them is not a plot
    const merged = union(...picked.map((k) => grow([[imp.outlines![k].ring]], 0.02)));
    if (merged.length !== 1) { message("Parcelele alese nu se ating: nu se pot comasa într-un singur teren.", true); r.checked = false; return; }
    useImported(imp.name, merged[0][0], picked.length > 1 ? [`Comasare din ${picked.length} parcele (${fmt(picked.reduce((t, k) => t + imp.outlines![k].area, 0))} m² în acte).`] : [], imp.outlines, picked[0]);
    state.imported!.chosenSet = picked;
    paintOutlinePick();
  }));
}

/**
 * Neighbouring buildings from OpenStreetMap. It is the one call that leaves
 * the browser, so it asks first and says what goes out: a rectangle around
 * the plot, not the plot's outline or anything typed about it.
 */
async function loadNeighbours() {
  const origin = state.imported?.plot.origin;
  if (!origin || !origin[0]) return;
  if (!confirm("Trimit către OpenStreetMap (Overpass) un dreptunghi de aproximativ 250 m în jurul terenului, ca să aduc clădirile din jur. Conturul terenului și regulile rămân în browser. Continui?")) return;
  const btn = $("#osm");
  btn.textContent = "Se încarcă vecinii…";
  try {
    const res = await fetch("https://overpass-api.de/api/interpreter", { method: "POST", body: "data=" + encodeURIComponent(overpassQuery(state.site.boundary, origin)), headers: { "Content-Type": "application/x-www-form-urlencoded" } });
    if (!res.ok) throw new Error(`Overpass a răspuns ${res.status}`);
    const nb = neighboursFrom(await res.json(), origin, [[state.site.boundary]]);
    state.site.context = nb;
    const assumed = nb.filter((x) => x.heightFrom === "assumed").length;
    message(`${nb.length} clădiri vecine încărcate${assumed ? `; ${assumed} fără înălțime în OpenStreetMap, socotite de 10 m` : ""}. Însorirea și umbrirea le iau în calcul.`);
    schedule();
  } catch (e) {
    message(`Nu am putut încărca vecinii: ${(e as Error).message}`, true);
  } finally { btn.textContent = "Încarcă clădirile vecine (OpenStreetMap)"; }
}

function paintPlotInfo() {
  const root = $("#plotInfo");
  const imp = state.imported;
  const osm = document.getElementById("osm");
  if (osm) osm.hidden = !(state.siteId === "import" && imp && imp.plot.origin[0] > 50_000);
  if (state.siteId !== "import" || !imp) { root.innerHTML = ""; return; }
  const [e, n] = imp.plot.origin;
  root.innerHTML = `<div class="kpis" style="margin-top:8px">
    <div><span>Suprafață măsurată</span><b>${fmt(imp.plot.area, 1)} m²</b></div>
    <div><span>Origine Stereo 70</span><b style="font-size:var(--small)">E ${fmt(e)} · N ${fmt(n)}</b></div></div>
    <div class="sub" style="margin-top:4px">Compară suprafața cu cea din extrasul CF înainte de a te baza pe POT și CUT.</div>`;
}

const KIND_LABEL: [EdgeKind, string][] = [["aliniament", "Stradă"], ["lateral", "Lateral"], ["posterior", "Spate"]];

function paintEdges() {
  const root = $("#edgesBox");
  const r = state.site.boundary;
  root.innerHTML = `<h2>Laturi</h2><div class="sub">Marchează latura (laturile) la stradă; spatele și lateralele se propun singure, le poți corecta.</div>`;
  const table = document.createElement("div");
  table.className = "edges";
  r.forEach((_, i) => {
    const row = document.createElement("div");
    row.className = "edge";
    row.innerHTML = `<span class="tag k-${state.site.edges[i]}">L${i + 1}</span><span class="len">${fmt(edgeLength(r, i), 1)} m</span>`;
    row.appendChild(seg(KIND_LABEL, state.site.edges[i], (v) => {
      const wasStreet = state.site.edges[i] === "aliniament";
      if (v === "aliniament" && state.streetGuessed) state.site.edges = state.site.edges.map((k) => (k === "aliniament" ? "lateral" : k));
      if (v === "aliniament") state.streetGuessed = false;
      state.site.edges[i] = v;
      // the street decides what is rear: re-suggest the others when it changes
      if (wasStreet !== (v === "aliniament")) {
        const street = state.site.edges.map((k, j) => (k === "aliniament" ? j : -1)).filter((j) => j >= 0);
        if (street.length) state.site.edges = suggestEdges(r, street);
      }
      paintEdges();
      schedule();
    }));
    table.appendChild(row);
  });
  root.appendChild(table);
}

function paintMixWarnings() {
  state.prog.mix.forEach((m, i) => {
    const el = document.getElementById(`mixwarn${i}`);
    if (el) el.textContent = m.area < MIN_DWELLING_AREA[m.type] ? `sub minimul legal de ${MIN_DWELLING_AREA[m.type]} m² (neverificat)` : "";
  });
}

// ---------- generation ----------

let timer = 0;
function schedule() { clearTimeout(timer); timer = window.setTimeout(run, 120); }

function normalisedProgramme(): Programme {
  const total = state.prog.mix.reduce((t, m) => t + m.share, 0) || 1;
  const norm = (mix: Programme["mix"]) => { const t = mix.reduce((x, m) => x + m.share, 0) || 1; return mix.filter((m) => m.share > 0).map((m) => ({ ...m, share: m.share / t })); };
  void total;
  return {
    ...state.prog,
    mix: norm(state.prog.mix),
    // a range takes its areas from the main mix, so one place sets what a "3 camere" is
    mixByLevel: (state.prog.mixByLevel ?? []).map((r) => ({ ...r, mix: norm(r.mix.map((m) => ({ ...m, area: state.prog.mix.find((x) => x.type === m.type)?.area ?? m.area }))) })),
  };
}

const keyOf = (g: Generated) => `${g.option.typology}:${g.option.along.join()}`;

function run() {
  autosave();
  if (state.mode === "retail") { R.run(); return; }
  const site = state.site;
  const t = performance.now();
  try {
    result = generate(site, state.rules, normalisedProgramme(), 6);
  } catch (e) {
    console.error(e);
    result = null;
  }
  const ms = performance.now() - t;
  sortOptions();
  const opts = result?.options ?? [];
  current = opts.find((g) => keyOf(g) === state.optionKey) ?? opts[0] ?? null;
  if (current) state.optionKey = keyOf(current);
  if (current && state.level >= current.option.storeys.length) state.level = current.option.storeys.length - 1;
  if (current && state.level < 0 && state.level !== ALL && -state.level > current.parking.levels) state.level = current.parking.levels ? -current.parking.levels : ALL;
  paintOptions(ms);
  paintScene();
  paintResults();
}

// ---------- options strip ----------

const money = (n: number) => (Math.abs(n) >= 1e6 ? `${fmt(n / 1e6, 2)} mil. €` : `${fmt(n / 1e3, 0)} mii €`);
const pfCache = new WeakMap<Generated, ProForma>();
function pf(g: Generated): ProForma {
  if (!pfCache.has(g)) pfCache.set(g, proForma(g, result!.envelope.plotArea, state.finance));
  return pfCache.get(g)!;
}
/** The options in the order the user asked for; sun-ranked is the engine's own order. */
function sortOptions() {
  if (!result) return;
  const key: Record<typeof state.sortBy, (g: Generated) => number> = {
    sun: (g) => g.layout.sunlitUsable, profit: (g) => pf(g).profit, flats: (g) => g.dwellings.total, gross: (g) => g.option.grossArea, mix: (g) => -g.mixDistance,
  };
  result.options.sort((a, b) => key[state.sortBy](b) - key[state.sortBy](a));
}
/** Money changed: no new geometry, just new numbers. */
function repaintMoney() {
  if (!result) return;
  for (const g of result.options) pfCache.delete(g);
  sortOptions();
  paintOptions(0);
  paintResults();
}
function saveXlsx(file: string, sheet: Sheet) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([workbook(sheet) as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  a.download = file;
  a.click();
  URL.revokeObjectURL(a.href);
}
function download(name: string, text: string, type: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

function paintOptions(ms: number) {
  const root = $("#options");
  const opts = result?.options ?? [];
  if (opts.length === 0) {
    root.innerHTML = `<div class="empty">Nicio variantă încape în regulile astea. Încearcă un POT mai mare, retrageri mai mici sau tronsoane mai puțin adânci.</div>`;
    return;
  }
  const best = Math.max(...opts.map((g) => g.option.grossArea));
  root.innerHTML = opts.map((g, i) => {
    const o = g.option;
    return `<button class="opt ${g === current ? "on" : ""}" data-i="${i}">
      <b>${TYPOLOGY_NAME[o.typology]}</b> · P+${o.storeys.length - 1}E
      <div class="big">${g.dwellings.total} apartamente</div>
      <div class="m"><b class="${pf(g).profit >= 0 ? "pass" : "fail"}">${money(pf(g).profit)}</b> profit · marjă ${fmt(pf(g).margin * 100, 1)}% · ☀ ${g.layout.total - g.layout.sunless}/${g.layout.total}</div>
      <div class="m">${fmt(o.grossArea)} m² Adc · CUT ${fmt(g.indicators.cut, 2)} · POT ${fmt(g.indicators.pot, 1)}%</div>
      <div class="m">${o.typology === "tower" ? `latura ${fmt(o.depth, 0)} m` : `tronson ${fmt(o.depth, 1)} m`} · limitat de ${g.binding.join(", ")}</div>
      <div class="bar"><i style="width:${(o.grossArea / best) * 100}%"></i></div>
    </button>`;
  }).join("") + `<div class="optbar">
      <label class="sub">Ordonează după
        <select id="sortBy">
          <option value="sun">apartamente însorite</option><option value="profit">profit</option>
          <option value="flats">număr de apartamente</option><option value="gross">suprafață desfășurată</option><option value="mix">apropiere de mixul cerut</option>
        </select></label>
      <button class="btn" id="compare">Compară variantele</button>
      <div class="timing">${ms ? `${fmt(ms)} ms` : ""}</div></div>`;
  const sel = $("#sortBy") as HTMLSelectElement;
  sel.value = state.sortBy;
  sel.onchange = () => { state.sortBy = sel.value as typeof state.sortBy; sortOptions(); paintOptions(0); };
  $("#compare").onclick = showComparison;
  root.querySelectorAll<HTMLButtonElement>(".opt").forEach((b) => (b.onclick = () => {
    current = opts[+b.dataset.i!];
    state.optionKey = keyOf(current);
    if (state.level >= current.option.storeys.length) state.level = current.option.storeys.length - 1;
    if (state.level < 0 && state.level !== ALL && -state.level > current.parking.levels) state.level = ALL;
    paintOptions(ms); paintScene(); paintResults();
  }));
}

// ---------- results ----------

function paintResults() {
  const root = $("#results");
  if (!current || !result) { root.innerHTML = `<div class="empty">—</div>`; return; }
  const g = current, o = g.option, d = g.dwellings, p = g.parking;
  const kpi = (k: string, v: string) => `<div><span>${k}</span><b>${v}</b></div>`;
  root.innerHTML = `
    <h2 style="margin-top:0">${TYPOLOGY_NAME[o.typology]} · P+${o.storeys.length - 1}E · ${fmt(o.height, 1)} m</h2>
    <div class="sub" style="margin-bottom:8px">Ce oprește clădirea să crească: <b>${g.binding.join(", ")}</b>. ${fmt(g.dualAspect * 100, 0)}% din apartamente au ferestre pe două orientări; mixul se abate cu ${fmt(g.mixDistance * 100, 0)}% de la cel cerut.</div>
    ${(() => {
      const n = (v: string) => g.checks.filter((c) => c.verdict === v).length;
      const fail = n("fail"), todo = n("not-checked");
      return `<a class="verdict ${fail ? "fail" : "pass"}" href="#compliance">${fail ? `${fail} ${fail === 1 ? "regulă încălcată" : "reguli încălcate"}` : "Respectă toate regulile verificate"} · ${n("pass")} în regulă${todo ? ` · ${todo} de verificat de tine` : ""} ↓</a>`;
    })()}
    <div class="gauges">${(() => {
      const L = limits(state.rules, result.envelope.plotArea), lim = { pot: L.potLimit, cut: L.cutLimit };
      const gauge = (k: string, used: number, max: number, unit = "") => {
        const r = max ? used / max : 0;
        // green: the limit is well used; amber: potential left on the table; red: over
        const cls = r > 1.0001 ? "over" : r >= 0.9 ? "good" : "under";
        return `<div class="gauge ${cls}"><div class="glabel"><span>${k}</span><b>${fmt(used, unit === "" ? 2 : 1)}${unit} / ${fmt(max, unit === "" ? 2 : 1)}${unit}</b></div><div class="gbar"><i style="width:${Math.min(100, r * 100)}%"></i></div></div>`;
      };
      return gauge("POT", g.indicators.pot, lim.pot, "%") + gauge("CUT", g.indicators.cut, lim.cut) + gauge("Înălțime", o.height, state.rules.maxHeight, " m") +
        (state.rules.minGreen ? gauge("Spațiu verde (min)", (g.site.balance.green / g.site.balance.plot) * 100, state.rules.minGreen, "%") : "");
    })()}</div>
    <div class="kpis">
      ${kpi("Teren", fmt(result.envelope.plotArea) + " m²")}
      ${kpi("Amprentă (Ac)", fmt(o.footprintArea) + " m²")}
      ${kpi("Desfășurată (Adc)", fmt(o.grossArea) + " m²")}
      ${kpi("Utilă locuințe", fmt(d.usable) + " m²")}
      ${kpi((state.rules.regime ?? "catuc") === "catuc" ? "POT (cu balcoane)" : "POT", fmt(g.indicators.pot, 1) + "%")}
      ${kpi("CUT", fmt(g.indicators.cut, 2))}
      ${kpi("Apartamente", String(d.total))}
      ${kpi("Parcare", `${p.capacity} locuri · ${p.levels} ${p.levels === 1 ? "subsol" : "subsoluri"}`)}
    </div>
    <h2>Apartamente</h2>
    <table class="t">${d.byType.map((b) => `<tr><td><span class="swatch" style="background:${css(TYPE_COLOUR[b.type])}"></span>${TYPE_NAME[b.type]}</td><td class="n">${b.count}</td><td class="n src">${b.count ? `medie ${fmt(b.area)} m² · min ${fmt(b.minUsable ?? 0)}` : ""}</td></tr>`).join("")}
      <tr><td><b>Total</b></td><td class="n">${d.total}</td><td class="n src">${g.layout.cores} nuclee scară + lift</td></tr></table>
    <h2>Financiar</h2>
    <table class="t">${(() => {
      const f = pf(g);
      const row = (k: string, v: number, note = "") => `<tr><td>${k}${note ? `<div class="src">${note}</div>` : ""}</td><td class="n">${money(v)}</td></tr>`;
      return row("Venit apartamente", f.revenue.flats) + (f.revenue.retail ? row("Venit comercial", f.revenue.retail) : "") + row("Venit parcare", f.revenue.parking, `${g.parking.capacity} locuri`) +
        `<tr><td><b>Venit total</b></td><td class="n">${money(f.revenue.total)}</td></tr>` +
        row("Teren", f.cost.land) + row("Construcție supraterană", f.cost.hardAbove) + row("Construcție subsol", f.cost.hardBelow) + row("Indirecte și neprevăzute", f.cost.soft + f.cost.contingency) +
        `<tr><td><b>Cost total</b></td><td class="n">${money(f.cost.total)}</td></tr>` +
        `<tr><td><b>Profit</b><div class="src">marjă ${fmt(f.margin * 100, 1)}% · pe cost ${fmt(f.onCost * 100, 1)}% · ${money(f.costPerFlat)} cost / apartament</div></td><td class="n ${f.profit >= 0 ? "pass" : "fail"}">${money(f.profit)}</td></tr>` +
        `<tr><td><b>Cât poți plăti pe teren</b><div class="src">valoarea reziduală la o marjă de ${fmt(state.targetMargin, 0)}% · ${fmt(residualLand(g, result!.envelope.plotArea, state.finance, state.targetMargin / 100) / result!.envelope.plotArea, 0)} € / m² de teren</div></td><td class="n">${money(residualLand(g, result!.envelope.plotArea, state.finance, state.targetMargin / 100))}</td></tr>`;
    })()}</table>
    <table class="t sens">${(() => {
      const m = sensitivity(g, result!.envelope.plotArea, state.finance);
      const head = `<tr><th>Profit dacă</th><th>cost −10%</th><th>cost așa</th><th>cost +10%</th></tr>`;
      return head + ["prețuri −10%", "prețuri așa", "prețuri +10%"].map((k, i) => `<tr><td>${k}</td>${m[i].map((v) => `<td class="n ${v >= 0 ? "pass" : "fail"}">${money(v)}</td>`).join("")}</tr>`).join("");
    })()}</table>
    <h2>Export</h2>
    <div class="row"><button class="btn" id="expDxf">DXF planuri</button><button class="btn" id="expIfc">IFC (BIM)</button></div>
    <div class="row" style="margin-top:6px"><button class="btn" id="expFlats">Excel apartamente</button><button class="btn" id="expOpts">Excel variante</button></div>
    <div class="row" style="margin-top:6px"><button class="btn" id="expFlatsCsv">CSV apartamente</button><button class="btn" id="expOptsCsv">CSV variante</button></div>
    <div class="sub" style="margin-top:4px">${state.imported?.plot.origin[0] ? "DXF-ul e în Stereo 70, peste planul cadastral." : "DXF-ul e în coordonate locale; importă terenul ca să-l primești în Stereo 70."}</div>
    <h2>Bilanț teritorial</h2>
    <table class="t">${(() => {
      const b = g.site.balance, pct = (x: number) => fmt((x / b.plot) * 100, 1) + "%";
      const row = (k: string, v: number, note = "") => `<tr><td>${k}${note ? `<div class="src">${note}</div>` : ""}</td><td class="n">${fmt(v)} m²</td><td class="n src">${pct(v)}</td></tr>`;
      return row("Construit la sol (Ac)", b.built) + row("Circulații (alei, rampă)", b.paved) +
        row("Spații verzi", b.green, `din care pe teren natural ${fmt(b.greenNatural)} m² (${pct(b.greenNatural)})`) +
        `<tr><td><b>Teren</b></td><td class="n">${fmt(b.plot)} m²</td><td class="n src">100%</td></tr>`;
    })()}</table>
    <h2>Însorire</h2>
    <table class="t"><tr><td>Camera de zi ≥ 1½ h la 21 decembrie</td><td class="n ${g.layout.sunless ? "fail" : "pass"}">${g.layout.total - g.layout.sunless} / ${g.layout.total}</td></tr></table>
    <h2>Parcare</h2>
    <table class="t">
      <tr><td>Necesar</td><td class="n">${p.required}</td><td class="n src">${fmt(p.perDwelling, 1)} / apartament</td></tr>
      ${p.levels ? `<tr><td>Pe un subsol</td><td class="n">${p.perLevel}</td><td class="n src">${fmt(p.basementArea / Math.max(1, p.perLevel), 1)} m² / loc</td></tr>` : ""}
      ${p.surface ? `<tr><td>La sol</td><td class="n">${p.surface}</td><td class="n src">locuri</td></tr>` : ""}
      ${p.podium ? `<tr><td>Podium</td><td class="n">${p.podium}</td><td class="n src">${state.prog.parking.podiumLevels} niveluri, în CUT</td></tr>` : ""}
      <tr><td>Subsoluri</td><td class="n">${p.levels}</td><td class="n src">${p.underground} locuri</td></tr>
      <tr><td><b>Total</b></td><td class="n ${p.short ? "fail" : "pass"}">${p.capacity}</td><td class="n src">${p.short ? `lipsesc ${p.required - p.capacity}` : `necesar ${p.required}`}</td></tr>
      <tr><td>Persoane cu dizabilități / încărcare electrică</td><td class="n">${p.accessible} / ${p.ev}</td><td class="n src">neverificat</td></tr></table>
    <h2 id="compliance">Conformitate</h2>
    <table class="t">${g.checks.map((c) => `<tr><td>${c.rule}${c.verified ? "" : `<span class="unv" title="Regula nu a fost verificată în textul oficial">neverificat</span>`}<div class="src">${c.source}</div>${c.note ? `<div class="src">${c.note}</div>` : ""}</td><td class="n ${c.verdict}">${c.value}<div class="src">${c.limit}</div></td></tr>`).join("")}</table>`;
  const name = (state.imported?.name ?? sampleName(state.siteId) ?? "teren").replace(/[^\p{L}\p{N}]+/gu, "-").toLowerCase();
  $("#expDxf").onclick = () => download(`${name}-${o.id}.dxf`, optionDxf(g, result!.envelope, state.imported?.plot.origin ?? [0, 0]), "application/dxf");
  $("#expFlatsCsv").onclick = () => download(`${name}-${o.id}-apartamente.csv`, flatsCsv(g), "text/csv");
  $("#expOptsCsv").onclick = () => download(`${name}-variante.csv`, optionsCsv(result!.options.map((x) => ({ g: x, pf: pf(x) })), result!.envelope.plotArea), "text/csv");
  $("#expIfc").onclick = () => download(`${name}-${o.id}.ifc`, optionIfc(g, state.imported?.name ?? name, state.imported?.plot.origin ?? [0, 0]), "application/x-step");
  const xlsx = saveXlsx;
  $("#expFlats").onclick = () => xlsx(`${name}-${o.id}-apartamente.xlsx`, {
    name: "Apartamente", preamble: [`${state.imported?.name ?? name} · ${TYPOLOGY_NAME[o.typology]} P+${o.storeys.length - 1}E`, `Development Assistant ${__COMMIT__}`],
    columns: [{ header: "Nivel", width: 8 }, { header: "Apartament", width: 11 }, { header: "Camere", width: 8 }, { header: "Construită m²", format: "money" }, { header: "Utilă m²", format: "money" }, { header: "Ore soare 21 dec.", format: "money" }, { header: "Preț estimat €", format: "money", width: 16 }],
    rows: g.layout.floors.flatMap((f) => f.units.filter((u) => u.kind === "apartment").map((u) => [f.index === 0 ? "P" : `E${f.index}`, u.id, Number(u.type), u.area, u.usable ?? 0, u.sunHours ?? 0, (u.usable ?? 0) * state.finance.salePerM2[u.type!]])),
    footer: ["Total", `${g.dwellings.total}`, null, g.layout.floors.flatMap((f) => f.units).filter((u) => u.kind === "apartment").reduce((t, u) => t + u.area, 0), g.dwellings.usable, null, pf(g).revenue.flats],
  });
  $("#expOpts").onclick = () => xlsx(`${name}-variante.xlsx`, {
    name: "Variante", preamble: [`${state.imported?.name ?? name} · teren ${fmt(result!.envelope.plotArea)} m²`, `Development Assistant ${__COMMIT__}`],
    columns: [{ header: "Variantă", width: 26 }, { header: "Regim", width: 8 }, { header: "Apartamente" }, { header: "Însorite" }, { header: "Adc m²", format: "money" }, { header: "Utilă m²", format: "money" }, { header: "CUT", format: "money" }, { header: "POT %", format: "money" }, { header: "Parcare" }, { header: "Venit €", format: "money", width: 16 }, { header: "Cost €", format: "money", width: 16 }, { header: "Profit €", format: "money", width: 16 }, { header: "Marjă", format: "pct" }],
    rows: result!.options.map((x) => { const f = pf(x); return [`${TYPOLOGY_NAME[x.option.typology]} · tronson ${fmt(x.option.depth, 1)} m`, `P+${x.option.storeys.length - 1}E`, x.dwellings.total, x.layout.total - x.layout.sunless, x.option.grossArea, x.dwellings.usable, x.indicators.cut, x.indicators.pot, x.parking.capacity, f.revenue.total, f.cost.total, f.profit, f.margin]; }),
  });
}

/** Every option side by side, sortable by any column: TestFit's "schemes", with our numbers. */
function showComparison() {
  if (!result) return;
  const cols: { k: string; v: (g: Generated) => number; f: (n: number) => string; low?: boolean }[] = [
    { k: "Apartamente", v: (g) => g.dwellings.total, f: (n) => fmt(n) },
    { k: "Însorite", v: (g) => g.layout.total - g.layout.sunless, f: (n) => fmt(n) },
    { k: "Adc m²", v: (g) => g.option.grossArea, f: (n) => fmt(n) },
    { k: "Utilă m²", v: (g) => g.dwellings.usable, f: (n) => fmt(n) },
    { k: "CUT", v: (g) => g.indicators.cut, f: (n) => fmt(n, 2) },
    { k: "POT %", v: (g) => g.indicators.pot, f: (n) => fmt(n, 1) },
    { k: "Parcare", v: (g) => g.parking.capacity, f: (n) => fmt(n) },
    { k: "Venit", v: (g) => pf(g).revenue.total, f: money },
    { k: "Cost", v: (g) => pf(g).cost.total, f: money, low: true },
    { k: "Profit", v: (g) => pf(g).profit, f: money },
    { k: "Marjă %", v: (g) => pf(g).margin * 100, f: (n) => fmt(n, 1) },
  ];
  let sortCol = 9, desc = true;
  const dlg = document.createElement("dialog");
  dlg.className = "compare";
  const paint = () => {
    const rows = [...result!.options].sort((a, b) => (desc ? -1 : 1) * (cols[sortCol].v(a) - cols[sortCol].v(b)));
    const best = cols.map((c) => (c.low ? Math.min : Math.max)(...rows.map(c.v)));
    dlg.innerHTML = `<div class="dhead"><b>Comparație variante</b><button class="btn" id="dclose">Închide</button></div>
      <table class="t"><tr><th>Variantă</th>${cols.map((c, i) => `<th data-c="${i}" class="${i === sortCol ? "on" : ""}">${c.k}${i === sortCol ? (desc ? " ↓" : " ↑") : ""}</th>`).join("")}</tr>
      ${rows.map((g) => `<tr data-k="${keyOf(g)}"><td>${TYPOLOGY_NAME[g.option.typology]} · P+${g.option.storeys.length - 1}E<div class="src">tronson ${fmt(g.option.depth, 1)} m</div></td>${cols.map((c, i) => `<td class="n ${c.v(g) === best[i] ? "best" : ""}">${c.f(c.v(g))}</td>`).join("")}</tr>`).join("")}</table>
      <div class="sub">Click pe o coloană ca să ordonezi, pe un rând ca să deschizi varianta. Cel mai bun pe fiecare coloană e marcat.</div>`;
    dlg.querySelector("#dclose")!.addEventListener("click", () => dlg.close());
    dlg.querySelectorAll<HTMLElement>("th[data-c]").forEach((th) => (th.onclick = () => { const c = +th.dataset.c!; desc = c === sortCol ? !desc : true; sortCol = c; paint(); }));
    dlg.querySelectorAll<HTMLElement>("tr[data-k]").forEach((tr) => (tr.onclick = () => {
      const g = result!.options.find((x) => keyOf(x) === tr.dataset.k);
      if (g) { current = g; state.optionKey = keyOf(g); paintOptions(0); paintScene(); paintResults(); }
      dlg.close();
    }));
  };
  paint();
  dlg.addEventListener("close", () => dlg.remove());
  document.body.appendChild(dlg);
  dlg.showModal();
}

// ---------- scene ----------

const canvas = $("#c") as unknown as HTMLCanvasElement;
// preserveDrawingBuffer: the report takes pictures of the canvas
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(35, 1, 0.5, 3000);
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.maxPolarAngle = Math.PI / 2 - 0.05;
scene.add(new THREE.HemisphereLight(0xffffff, 0xaab0ba, 1.7));
const sun = new THREE.DirectionalLight(0xffffff, 1.5);
sun.position.set(-80, 140, 100);
scene.add(sun);

const ground = new THREE.Mesh(new THREE.PlaneGeometry(2000, 2000), new THREE.MeshLambertMaterial({ color: 0xe3e6ea }));
ground.rotation.x = -Math.PI / 2;
ground.position.y = -0.06;
scene.add(ground);

const world = new THREE.Group();
scene.add(world);

function shapeOf(poly: Ring[]): THREE.Shape {
  const s = new THREE.Shape(poly[0].map(([x, y]) => new THREE.Vector2(x, y)));
  for (const h of poly.slice(1)) s.holes.push(new THREE.Path(h.map(([x, y]) => new THREE.Vector2(x, y))));
  return s;
}
const matCache = new Map<string, THREE.Material>();
function mat(colour: number, opacity = 1): THREE.Material {
  const k = `${colour}:${opacity}`;
  if (!matCache.has(k)) matCache.set(k, new THREE.MeshLambertMaterial({ color: colour, transparent: opacity < 1, opacity, depthWrite: opacity === 1 }));
  return matCache.get(k)!;
}
const edgeMat = new THREE.LineBasicMaterial({ color: 0x1f2937, transparent: true, opacity: 0.35 });

/** Plan (x, y) to world (x, -y) with y up: the extrusion becomes height. */
function prism(mp: MultiPoly, base: number, h: number, m: THREE.Material, edges = true): THREE.Group {
  const g = new THREE.Group();
  for (const poly of mp) {
    const geo = new THREE.ExtrudeGeometry(shapeOf(poly), { depth: h, bevelEnabled: false });
    geo.rotateX(-Math.PI / 2);
    const mesh = new THREE.Mesh(geo, m);
    mesh.position.y = base;
    g.add(mesh);
    if (edges) {
      const e = new THREE.LineSegments(new THREE.EdgesGeometry(geo, 25), edgeMat);
      e.position.y = base;
      g.add(e);
    }
  }
  return g;
}
function outline(ring: Ring, y: number, colour: number): THREE.Line {
  const pts = [...ring, ring[0]].map(([x, z]) => new THREE.Vector3(x, y, -z));
  return new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: colour }));
}
function disposeGroup(g: THREE.Object3D) {
  g.traverse((o) => { if ((o as THREE.Mesh).geometry) (o as THREE.Mesh).geometry.dispose(); });
  g.clear();
}

const unitColour = (u: Unit) => (u.kind === "apartment" ? TYPE_COLOUR[u.type!] : KIND_COLOUR[u.kind]);

interface Label { el: HTMLElement; at: THREE.Vector3 }
let handles: THREE.Mesh[] = [];
/** Flats in the scene, for picking: click one and its plan opens. */
let pickables: THREE.Object3D[] = [];
const tag = <T extends THREE.Object3D>(o: T, u: Unit): T => { o.traverse((x) => (x.userData.unit = u.id)); pickables.push(o); return o; };
let labels: Label[] = [];

function centroid(mp: MultiPoly): [number, number] {
  let best = mp[0], ba = 0;
  for (const p of mp) { const a = Math.abs(ringSigned(p[0])); if (a > ba) { ba = a; best = p; } }
  const r = best[0];
  let cx = 0, cy = 0, a2 = 0;
  for (let i = 0; i < r.length; i++) {
    const [x1, y1] = r[i], [x2, y2] = r[(i + 1) % r.length];
    const c = x1 * y2 - x2 * y1;
    a2 += c; cx += (x1 + x2) * c; cy += (y1 + y2) * c;
  }
  return a2 === 0 ? r[0] : [cx / (3 * a2), cy / (3 * a2)];
}
function ringSigned(r: Ring) { let s = 0; for (let i = 0; i < r.length; i++) { const [x1, y1] = r[i], [x2, y2] = r[(i + 1) % r.length]; s += x1 * y2 - x2 * y1; } return s / 2; }

function label(html: string, cls: string, at: THREE.Vector3) {
  const el = document.createElement("div");
  el.className = "lbl " + cls;
  el.innerHTML = html;
  $("#labels").appendChild(el);
  labels.push({ el, at });
}

/** A small round marker standing on a floor: a front door or an entrance. */
function marker(p: [number, number], y: number, colour: number, r = 0.35, h = 0.3) {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 16), mat(colour));
  m.position.set(p[0], y + h / 2, -p[1]);
  world.add(m);
}

const PART_COLOUR = { stair: 0x7b8494, lift: 0x1f2937, landing: 0xd7dce3, shaft: 0xa3aab5 } as const;
const PART_LABEL = { stair: "scară", lift: "lift", landing: "", shaft: "" } as const;

function paintCore(u: Unit, base: number, h: number, isolated: boolean, ground: boolean) {
  if (!isolated || !u.parts?.length) { world.add(prism(u.polygon, base, h, mat(KIND_COLOUR.core), true)); return; }
  for (const p of u.parts) {
    const ph = p.kind === "lift" ? 2.6 : p.kind === "stair" ? 1.6 : p.kind === "shaft" ? 1.0 : 0.15;
    world.add(prism(p.polygon, base, ph, mat(PART_COLOUR[p.kind]), true));
    const text = p.kind === "shaft" && ground ? "hol" : PART_LABEL[p.kind];
    if (text) { const [x, y] = centroid(p.polygon); label(text, "core", new THREE.Vector3(x, base + ph + 0.3, -y)); }
  }
}

function paintBasement(level: number) {
  const pl = current!.parking.plan.levels.find((l) => l.index === level);
  if (!pl) return;
  ground.visible = false;
  const base = pl.base;
  world.add(prism(pl.footprint, base - 0.3, 0.3, mat(0x8a919c), false));
  for (const p of pl.footprint) world.add(outline(p[0], base + 0.02, 0x374151));
  world.add(prism(pl.aisles, base, 0.04, mat(0x6b7280), false));
  for (const st of pl.stalls) {
    world.add(prism(st, base, 0.06, mat(0xe8ebef), false));
    world.add(outline(st[0][0], base + 0.08, 0x4b5563));
  }
  world.add(prism(pl.ramp, base, 0.5, mat(0xe0a23b), true));
  if (pl.ramp.length) { const [x, y] = centroid(pl.ramp); label(level === -1 ? "rampă ↑ stradă" : "rampă", "ramp", new THREE.Vector3(x, base + 0.8, -y)); }
  // the cores go down: stair and lifts are drawn as on the floors above
  for (const c of current!.layout.floors[1]?.units.filter((u) => u.kind === "core") ?? current!.layout.floors[0].units.filter((u) => u.kind === "core")) {
    paintCore(c, base, 2.8, true, false);
  }
}

const OPENING_COLOUR = { window: 0x38bdf8, balconyDoor: 0x0284c7, shopfront: 0x38bdf8, frontDoor: 0x7c2d12, door: 0xd97706, entrance: 0x16a34a } as const;

/** A thin bar lying along a wall segment: how an opening reads in plan. */
function segBar(a: [number, number], b: [number, number], y: number, h: number, w: number, colour: number) {
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (L < 0.05) return;
  const m = new THREE.Mesh(new THREE.BoxGeometry(L, h, w), mat(colour));
  m.position.set((a[0] + b[0]) / 2, y + h / 2, -(a[1] + b[1]) / 2);
  m.rotation.y = Math.atan2(b[1] - a[1], b[0] - a[0]);
  world.add(m);
}

/**
 * Glass on the facades: one quad per window, balcony door, shopfront and
 * entrance, a few centimetres proud of the wall, all in one mesh. This is
 * what makes the massing read as a building rather than a stack of boxes.
 */
function glazing(o: Generated["option"], floors: Generated["layout"]["floors"], upTo: number) {
  const pos: number[] = [];
  for (const s of o.storeys) {
    if (s.index > upTo) continue;
    const h = s.cornice - s.base;
    for (const u of floors[s.index].units) for (const op of u.openings ?? []) {
      if (op.kind === "door" || op.kind === "frontDoor") continue;
      const [a, b] = [op.a, op.b];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < 0.3) continue;
      let nx = -(b[1] - a[1]) / L, ny = (b[0] - a[0]) / L;
      const m: [number, number] = [(a[0] + b[0]) / 2 + nx * 0.3, (a[1] + b[1]) / 2 + ny * 0.3];
      if (distanceTo(m, s.footprint) === 0) { nx = -nx; ny = -ny; }
      const off = 0.04;
      const [lo, hi] = op.kind === "window" ? [0.9, Math.min(2.3, h - 0.5)] : op.kind === "shopfront" ? [0.2, Math.min(3.4, h - 0.4)] : op.kind === "entrance" ? [0, Math.min(2.7, h - 0.4)] : [0.1, Math.min(2.3, h - 0.5)];
      const P = (p: [number, number], z: number) => [p[0] + nx * off, s.base + z, -(p[1] + ny * off)];
      const q = [P(a, lo), P(b, lo), P(b, hi), P(a, lo), P(b, hi), P(a, hi)];
      for (const v of q) pos.push(...v);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  const glass = new THREE.MeshLambertMaterial({ color: 0x2c4a66, side: THREE.DoubleSide });
  world.add(new THREE.Mesh(geo, glass));
}

function resetScene() {
  disposeGroup(world);
  pickables = [];
  handles = [];
  $("#labels").innerHTML = "";
  labels = [];
  ground.visible = true;
}

/** The plot, its neighbours, the handles on its corners and its edges: what both modes draw first. */
function paintSite(plot: MultiPoly) {
  world.add(prism(plot, 0, 0.03, mat(0xffffff), false));
  world.add(outline(plot[0][0], 0.06, 0x111827));
  // the neighbours, when loaded: pale, and paler where their height is a guess
  for (const nb of state.site.context ?? []) world.add(prism(nb.footprint, 0, nb.top, mat(nb.heightFrom === "assumed" ? 0xd1d5db : 0xb6bcc6, 0.85), true));
  // handles on the plot's corners: drag one and the building re-solves on release
  if (state.level === ALL || state.level === 0) {
    state.site.boundary.forEach(([x, y], i) => {
      const h = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.5, 20), mat(0xdc2626));
      h.position.set(x, 0.3, -y);
      h.userData.vertex = i;
      world.add(h);
      handles.push(h);
    });
  }
  const r = state.site.boundary;
  r.forEach((a, i) => {
    const b = r[(i + 1) % r.length];
    const kind = state.site.edges[i];
    world.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(a[0], 0.1, -a[1]), new THREE.Vector3(b[0], 0.1, -b[1])]),
      new THREE.LineBasicMaterial({ color: EDGE_COLOUR[kind] })));
    if (state.level === ALL) label(`L${i + 1}`, `edgetag k-${kind}`, new THREE.Vector3((a[0] + b[0]) / 2, 0.5, -(a[1] + b[1]) / 2));
  });
}

function paintScene() {
  if (state.mode === "retail") { R.paintScene(); return; }
  resetScene();
  if (!result) return;
  const env = result.envelope;

  if (current && state.level < 0 && state.level > -100) {
    paintBasement(state.level);
    paintLevels(); paintLegend(); paintFloorCard();
    return;
  }

  paintSite(env.plot);
  // the site plan: green, footpaths, the open ramp, the accesses
  if (current && (state.level === ALL || state.level === 0)) {
    const sp = current.site;
    world.add(prism(sp.green, 0.03, 0.04, mat(0x9bd39b), false));
    if (current.parking.surface) {
      world.add(prism(current.parking.surfaceAisles, 0.03, 0.05, mat(0x9ca3af), false));
      for (const st of current.parking.surfaceStalls) { world.add(prism(st, 0.03, 0.06, mat(0xe5e7eb), false)); world.add(outline(st[0][0], 0.1, 0x4b5563)); }
    }
    world.add(prism(sp.paths, 0.03, 0.06, mat(0xd9d4cc), false));
    world.add(prism(sp.rampOpen, 0.03, 0.08, mat(0xe0a23b), false));
    if (sp.vehicleAccess && current.parking.plan.levels[0]) {
      // an arrow from the street, along the ramp, down to the car park
      const ramp = current.parking.plan.levels[0].ramp;
      const [rx, ry] = centroid(ramp);
      const [ax, ay] = sp.vehicleAccess;
      const len = Math.hypot(rx - ax, ry - ay) || 1;
      const ux = (rx - ax) / len, uy = (ry - ay) / len, nx = -uy, ny = ux;
      const L = Math.min(len * 1.6, 16), w = 0.9, head = 2.6;
      const P = (s: number, t: number): [number, number] => [ax + ux * s + nx * t, ay + uy * s + ny * t];
      const arrow: MultiPoly = [[[P(0, -w), P(L - head, -w), P(L - head, -w * 2.2), P(L, 0), P(L - head, w * 2.2), P(L - head, w), P(0, w)]]];
      world.add(prism(arrow, 0.12, 0.08, mat(0xb45309), false));
      marker(sp.vehicleAccess, 0.05, 0xb45309, 0.9, 0.4);
      const [mx, my] = P(L * 0.75, 0);
      label(`Intrare parcare subterană ↓ S1<br>${current.parking.capacity} locuri`, "ramp", new THREE.Vector3(mx, 2.2, -my));
    }
    for (const p of sp.pedestrianAccess) marker(p, 0.05, 0x16a34a, 0.45, 0.3);
  }
  // The permitted volume *for this option*. A height-dependent setback
  // (H/2) gives a different footprint for every height, and drawing them all
  // stacked shows a stepped pyramid that is no building anyone may build: it
  // is the union of many alternatives. What the rules allow this option is
  // its main body at the setback its own cornice height requires, plus the
  // setback storeys at theirs, and the height limit above.
  if (state.envelope && current) {
    const st = current.option.storeys;
    let body = 1;
    while (body < st.length && Math.abs(st[body].area - st[0].area) < 0.5) body++;
    const boxes = [{ fp: env.storeys[body - 1].footprint, base: 0, top: env.storeys[body - 1].cornice }];
    for (let i = body; i < st.length && env.storeys[i]; i++) boxes.push({ fp: env.storeys[i].footprint, base: env.storeys[i].base, top: env.storeys[i].cornice });
    for (const b of boxes) {
      world.add(prism(b.fp, b.base, b.top - b.base, mat(0xf59e0b, 0.12), false));
      for (const p of b.fp) world.add(outline(p[0], b.top, 0xf59e0b));
    }
    const lid = env.storeys[env.storeys.length - 1];
    for (const p of lid.footprint) world.add(outline(p[0], state.rules.maxHeight, 0xb45309));
    if (state.level === ALL) {
      const [x, y] = centroid(lid.footprint);
      label(`Hmax ${fmt(state.rules.maxHeight, 1)} m`, "hmax", new THREE.Vector3(x, state.rules.maxHeight + 0.6, -y));
    }
  }

  if (current) {
    const o = current.option;
    const floors = current.layout.floors;
    for (const s of o.storeys) {
      const h = s.cornice - s.base;
      if (state.level >= 0 && s.index > state.level) continue;
      if (state.level >= 0 && s.index < state.level) {
        world.add(prism(s.footprint, s.base, h - 0.05, mat(0xc9ced6), true));
        continue;
      }
      // the storey slab, then each unit standing on it
      world.add(prism(s.footprint, s.base, 0.25, mat(0x9aa3af), false));
      const isolated = state.level === s.index;
      const floorY = s.base + 0.25;
      for (const u of floors[s.index].units) {
        if (u.kind === "parking") {
          // a podium floor: the car park inside the building
          world.add(prism(u.polygon, floorY, isolated ? 0.05 : h - 0.4, mat(isolated ? 0x6b7280 : 0x8b939e), !isolated));
          if (isolated) {
            for (const st of u.stalls ?? []) { world.add(prism(st, floorY + 0.05, 0.05, mat(0xe8ebef), false)); world.add(outline(st[0][0], floorY + 0.12, 0x4b5563)); }
            if (u.ramp?.length) world.add(prism(u.ramp, floorY, 0.4, mat(0xe0a23b), true));
            const [x, y] = centroid(u.polygon);
            label(`Parcare podium<br>${u.stalls?.length ?? 0} locuri`, "ramp", new THREE.Vector3(x, floorY + 1.2, -y));
          }
          continue;
        }
        if (u.kind === "core") paintCore(u, floorY, isolated ? 1.2 : h - 0.4, isolated, s.index === 0);
        else if (isolated && u.kind === "apartment" && u.rooms?.length) {
          // a flat on an isolated floor: its colour on the floor, its rooms drawn as walls
          world.add(tag(prism(u.polygon, floorY, 0.2, mat(unitColour(u)), false), u));
          for (const r of u.rooms) {
            if (r.kind === "bath" || r.kind === "hall" || r.kind === "storage") world.add(prism(r.polygon, floorY + 0.2, 0.05, mat(ROOM_TINT[r.kind]), false));
            for (const p of r.polygon) world.add(outline(p[0], floorY + 0.3, 0x1f2937));
            if (r.kind !== "hall" && r.kind !== "storage") { const [x, y] = centroid(r.polygon); label(ROOM_LABEL[r.kind], "room", new THREE.Vector3(x, floorY + 0.4, -y)); }
          }
          for (const p of u.polygon) world.add(outline(p[0], floorY + 0.32, (u.sunHours ?? 0) < SUN_HOURS ? 0xdc2626 : 0x111827));
          for (const op of u.openings ?? []) segBar(op.a, op.b, floorY + 0.2, 0.3, op.kind === "door" ? 0.18 : 0.32, OPENING_COLOUR[op.kind]);
        }
        else {
          const m = prism(u.polygon, floorY, isolated ? (u.kind === "corridor" ? 0.15 : 1.2) : h - 0.4, mat(unitColour(u)), true);
          world.add(u.kind === "apartment" ? tag(m, u) : m);
        }
        if (u.entrance) {
          marker(u.entrance, floorY, 0x16a34a, 0.6, isolated ? 1.6 : 0.6);
          if (isolated || state.level === ALL) label("Intrare", "entry", new THREE.Vector3(u.entrance[0], floorY + (isolated ? 2.4 : 1.2), -u.entrance[1]));
        }
        if (u.balcony) {
          world.add(prism(u.balcony, floorY - 0.1, 0.12, mat(0xb8c2cf), false));
          for (const p of u.balcony) world.add(outline(p[0], floorY + (isolated ? 1.1 : 1.0), 0x475569));
        }
        if (!isolated) continue;
        const [x, y] = centroid(u.polygon);
        const at = new THREE.Vector3(x, floorY + 1.4, -y);
        if (u.kind === "apartment") {
          const sunny = (u.sunHours ?? 0) >= SUN_HOURS;
          label(`<b>${u.type === "1" ? "G" : u.type + "C"}</b> ${fmt(u.usable!)} m²<br><span class="${sunny ? "" : "nosun"}">☀ ${fmt(u.sunHours ?? 0, 1)} h</span>`, "flat", new THREE.Vector3(x, floorY + 1.6, -y));
        }
        else if (u.kind === "retail") label(`<b>Com.</b><br>${fmt(u.area)} m²`, "", at);
        else if (u.kind === "corridor") label("coridor", "corr", new THREE.Vector3(x, floorY + 0.4, -y));
      }
    }
  }
  if (current) {
    // openings on the isolated floor's shops and cores; glass on every facade otherwise
    if (state.level >= 0) {
      const s = current.option.storeys[state.level];
      for (const u of current.layout.floors[state.level].units) if (u.kind !== "apartment") for (const op of u.openings ?? []) segBar(op.a, op.b, s.base + 0.45, 0.3, 0.32, OPENING_COLOUR[op.kind]);
      if (state.level > 0) glazing(current.option, current.layout.floors, state.level - 1);
    } else glazing(current.option, current.layout.floors, Infinity);
  }
  paintLevels();
  paintLegend();
  paintFloorCard();
}

function paintLevels() {
  const root = $("#levels");
  if (!current) { root.innerHTML = ""; return; }
  const o = current.option;
  const body = o.storeys[Math.min(1, o.storeys.length - 1)].area;
  const btn = (i: number, label: string) => `<button data-l="${i}" class="${state.level === i ? "on" : ""}">${label}</button>`;
  root.innerHTML = btn(ALL, "Tot") + [...o.storeys].reverse().map((s) =>
    btn(s.index, (s.index === 0 ? "P" : `E${s.index}${s.index > 1 && s.area < body - 1 ? " r" : ""}`) + (current!.layout.floors[s.index]?.units.some((u) => u.kind === "parking") ? " ▣" : ""))).join("") +
    current.parking.plan.levels.map((l) => btn(l.index, `S${-l.index}`)).join("");
  root.querySelectorAll<HTMLButtonElement>("button").forEach((b) => (b.onclick = () => {
    state.level = +b.dataset.l!;
    frameCamera();
    paintScene();
  }));
}

function paintLegend() {
  const row = (c: number, t: string) => `<div><span class="swatch" style="background:${css(c)}"></span>${t}</div>`;
  if (state.level < 0 && state.level !== ALL) {
    $("#legend").innerHTML = row(0xe8ebef, "Loc de parcare 2,5 × 5 m") + row(0x6b7280, "Alee 6 m") + row(0xe0a23b, "Rampă") + row(PART_COLOUR.stair, "Scară") + row(PART_COLOUR.lift, "Lift");
    foldLegend();
    return;
  }
  const used = new Set<string>();
  const floors = current ? (state.level === ALL ? current.layout.floors : [current.layout.floors[state.level]]) : [];
  floors.forEach((f) => f.units.forEach((u) => used.add(u.kind === "apartment" ? u.type! : u.kind)));
  const isolated = state.level !== ALL;
  $("#legend").innerHTML =
    (Object.keys(TYPE_COLOUR) as UnitType[]).filter((t) => used.has(t)).map((t) => row(TYPE_COLOUR[t], TYPE_NAME[t])).join("") +
    (used.has("core") && !isolated ? row(KIND_COLOUR.core, "Nucleu: scară + lift") : "") +
    (used.has("core") && isolated ? row(PART_COLOUR.stair, "Scară") + row(PART_COLOUR.lift, "Lift") + row(PART_COLOUR.landing, "Palier") + row(PART_COLOUR.shaft, "Ghene, boxe, hol") : "") +
    (state.prog.balconyDepth > 0 && used.has("apartment") ? row(0xb8c2cf, "Balcon") : "") +
    (isolated && used.has("apartment") ? row(OPENING_COLOUR.window, "Fereastră") + row(OPENING_COLOUR.balconyDoor, "Ușă de balcon") + row(OPENING_COLOUR.frontDoor, "Ușa de intrare") + row(OPENING_COLOUR.door, "Ușă interioară") : "") +
    (!isolated ? row(0x2c4a66, "Ferestre, vitrine") : "") +
    (isolated && used.has("apartment") ? row(0xbfd7ea, "Baie") + `<div><span class="swatch" style="border:2px solid #dc2626;background:transparent"></span>Sub 1½ h soare la 21 dec.</div>` : "") +
    (state.level === ALL || state.level === 0 ? row(0x9bd39b, "Spațiu verde") + row(0xd9d4cc, "Alei pietonale") + row(0xe0a23b, "Rampă spre parcarea subterană") + row(0xb45309, "Acces auto din stradă") : "") +
    (current?.layout.floors[0].units.some((u) => u.entrance) && (state.level === ALL || state.level === 0) ? `<div><span class="swatch" style="background:#16a34a;border-radius:50%"></span>Intrare în clădire</div>` : "") +
    (used.has("corridor") ? row(KIND_COLOUR.corridor, "Coridor") : "") +
    (used.has("retail") ? row(KIND_COLOUR.retail, "Comercial") : "") +
    (state.envelope ? row(0xf59e0b, "Volum permis pentru această variantă") : "");
  foldLegend();
}

/** The legend folds to one word: the building is the picture, the key a footnote. */
function foldLegend() {
  const el = $("#legend");
  const open = stored("legend") ?? window.innerWidth > 760;
  el.innerHTML = `<details ${open ? "open" : ""}><summary>Legendă</summary><div class="lrows">${el.innerHTML}</div></details>`;
  el.querySelector("details")!.addEventListener("toggle", (e) => remember("legend", (e.target as HTMLDetailsElement).open));
}

function paintFloorCard() {
  const root = $("#floorcard");
  if (!current || state.level === ALL) { root.innerHTML = ""; return; }
  if (state.level < 0) {
    const p = current.parking;
    const lv = p.plan.levels.find((l) => l.index === state.level)!;
    root.innerHTML = `<div class="src">Subsolul ${-state.level} · cota ${fmt(lv.base, 2)} · ${fmt(p.basementArea)} m²</div>
      <b>${p.perLevel} locuri de parcare</b>
      <div class="src">${fmt(p.basementArea / Math.max(1, p.perLevel), 1)} m² pe loc · necesar total ${p.required} în ${p.levels} ${p.levels === 1 ? "subsol" : "subsoluri"}${p.short ? " — nu încap" : ""}</div>`;
    return;
  }
  const f = current.layout.floors[state.level];
  const flats = f.units.filter((u) => u.kind === "apartment");
  const s = current.option.storeys[state.level];
  const by = (Object.keys(TYPE_COLOUR) as UnitType[]).map((t) => [t, flats.filter((u) => u.type === t).length] as const).filter(([, n]) => n > 0);
  root.innerHTML = `<div class="src">${state.level === 0 ? "Parter" : `Etajul ${state.level}`} · cota +${fmt(s.base, 2)} · ${fmt(s.area)} m² construiți</div>
    <b>${flats.length ? `${flats.length} apartamente` : f.units.some((u) => u.kind === "parking") ? `Parcare podium · ${f.units.find((u) => u.kind === "parking")!.stalls?.length ?? 0} locuri` : f.units.some((u) => u.kind === "retail") ? "Spații comerciale" : "—"}</b>
    <div class="src">${by.map(([t, n]) => `${n} × ${TYPE_NAME[t].toLowerCase()}`).join(" · ")}</div>
    ${flats.length ? `<div class="src">${fmt(flats.reduce((t, u) => t + u.usable!, 0))} m² utili</div><button class="btn" id="flatPlans" style="width:100%;margin-top:6px">Planuri apartamente</button><div class="src" style="margin-top:4px">sau click pe un apartament</div>` : ""}`;
  const b = root.querySelector<HTMLButtonElement>("#flatPlans");
  if (b) b.onclick = () => showFlat(flats[0]);
}

function paintViewbar() {
  const root = $("#viewbar");
  root.innerHTML = "";
  root.appendChild(seg([["3d", "3D"], ["plan", "Plan"]] as ["3d" | "plan", string][], state.view, (v) => { state.view = v; frameCamera(); }));
  root.appendChild(seg([[1, "Volum permis"]] as [number, string][], state.envelope ? 1 : -1, () => { state.envelope = !state.envelope; paintViewbar(); paintScene(); }));
}

function frameCamera() {
  const site = state.site;
  const xs = site.boundary.map((p) => p[0]), ys = site.boundary.map((p) => p[1]);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cz = -(Math.min(...ys) + Math.max(...ys)) / 2;
  const span = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
  const y = !current || state.level === ALL ? 0
    : state.level >= 0 ? current.option.storeys[state.level].base
    : current.parking.plan.levels.find((l) => l.index === state.level)?.base ?? 0;
  if (state.view === "plan") {
    // fit the whole plot, both ways, whatever the pane's proportions
    const hx = (Math.max(...xs) - Math.min(...xs)) / 2, hz = (Math.max(...ys) - Math.min(...ys)) / 2;
    const tan = Math.tan((camera.fov * Math.PI) / 360);
    const dist = (Math.max(hz, hx / camera.aspect) / tan) * 1.2;
    camera.position.set(cx, y + dist, cz + 0.001);
    controls.target.set(cx, y, cz);
    controls.enableRotate = false;
  } else {
    const k = state.level !== ALL ? 1.15 : 1.5;
    camera.position.set(cx - span * 0.9 * k, y + span * 0.95 * k, cz + span * 1.1 * k);
    controls.target.set(cx, y + (state.level !== ALL ? 0 : 8), cz);
    controls.enableRotate = true;
  }
  controls.update();
}

function resize() {
  const r = canvas.parentElement!.getBoundingClientRect();
  renderer.setSize(r.width, r.height, false);
  camera.aspect = r.width / Math.max(1, r.height);
  camera.updateProjectionMatrix();
}

const v = new THREE.Vector3();
function placeLabels() {
  const r = canvas.getBoundingClientRect();
  for (const l of labels) {
    v.copy(l.at).project(camera);
    const hidden = v.z > 1;
    l.el.style.display = hidden ? "none" : "";
    l.el.style.position = "absolute";
    l.el.style.transform = `translate(-50%,-50%) translate(${((v.x + 1) / 2) * r.width}px, ${((1 - v.y) / 2) * r.height}px)`;
  }
}

function theme() {
  const dark = matchMedia("(prefers-color-scheme: dark)").matches;
  scene.background = new THREE.Color(dark ? 0x181b20 : 0xeef0f3);
  (ground.material as THREE.MeshLambertMaterial).color.set(dark ? 0x2a2e35 : 0xe3e6ea);
}

// ---------- what stays in this browser: projects, profile, brand ----------

interface PipeItem { name: string; savedAt: string; area: number; flats: number; profit: number; site: Site; rules: Rules; prog: Programme; finance: FinanceInputs; origin?: [number, number]; mode?: Mode; retail?: RetailProgramme; retailFinance?: RetailFinanceInputs; gla?: number }
function load<T>(key: string, fallback: T): T { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) as T : fallback; } catch { return fallback; } }
function save(key: string, v: unknown) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode or full: the page works without it */ } }
function esc(t: string) { return t.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!); }

/** The developer's candidate plots, side by side: TestFit's deals, Giraffe's pipeline. */
function paintPipeline() {
  const list = load<PipeItem[]>("da.cases", []);
  const root = document.getElementById("pipeline");
  if (!root) return;
  root.innerHTML = list.length ? `<table class="t">${list.map((c, i) => `<tr><td><a href="#" data-open="${i}">${esc(c.name)}</a><div class="src">${fmt(c.area)} m² · ${new Date(c.savedAt).toLocaleDateString("ro-RO")}</div></td><td class="n">${c.mode === "retail" ? `${fmt(c.gla ?? 0)} m² GLA` : `${c.flats} apt.`}<div class="src ${c.profit >= 0 ? "pass" : "fail"}">${money(c.profit)}</div></td><td><a href="#" data-del="${i}" title="Șterge">×</a></td></tr>`).join("")}</table>`
    : `<div class="sub">Niciun teren salvat încă.</div>`;
  root.querySelectorAll<HTMLAnchorElement>("[data-open]").forEach((a) => (a.onclick = (e) => {
    e.preventDefault();
    const c = list[+a.dataset.open!];
    state.rules = c.rules; state.prog = { ...DEFAULT_PROGRAMME, ...c.prog }; state.finance = { ...DEFAULT_FINANCE, ...c.finance };
    restoreRetail(c);
    state.imported = { name: c.name, plot: { ring: c.site.boundary, origin: c.origin ?? [0, 0], area: c.area, removed: 0, warnings: [] } };
    state.siteId = "import"; state.site = c.site;
    buildControls(); paintSitePicker(); newPlot();
  }));
  root.querySelectorAll<HTMLAnchorElement>("[data-del]").forEach((a) => (a.onclick = (e) => { e.preventDefault(); list.splice(+a.dataset.del!, 1); save("da.cases", list); paintPipeline(); }));
}

// ---------- share links ----------

/**
 * The whole case in the URL's fragment, deflated: no server keeps it, so
 * whoever has the link has the plot, and the page says so. The fragment is
 * never sent to the server that serves the page.
 */
async function encodeCase(): Promise<string> {
  const json = JSON.stringify({ v: 1, name: caseName(), mode: state.mode, site: state.site, rules: state.rules, prog: state.prog, finance: state.finance, retail: state.retail, retailFinance: state.retailFinance, origin: state.imported?.plot.origin });
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream("deflate-raw"));
  const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
async function decodeCase(s: string) {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return JSON.parse(await new Response(stream).text());
}
async function shareLink() {
  const url = `${location.origin}${location.pathname}#c=${await encodeCase()}`;
  try { await navigator.clipboard.writeText(url); message("Link copiat. Conține terenul și regulile: trimite-l doar cui are voie să le vadă."); }
  catch { prompt("Copiază linkul:", url); }
}
// An imported plot is work: a refresh, a closed tab or a crash must not
// throw it away. The current case is kept in this browser, the same encoding
// as the share link, and comes back when the page opens without a link.
const LAST = "da:last";
function autosave() {
  if (state.siteId !== "import") return;
  encodeCase().then((c) => { try { localStorage.setItem(LAST, c); } catch { /* private window */ } });
}
async function openFromLink() {
  const m = location.hash.match(/#c=([\w-]+)/);
  let last: string | null = null;
  try { last = localStorage.getItem(LAST); } catch { /* private window */ }
  const code = m?.[1] ?? last;
  if (!code) return;
  try {
    const data = await decodeCase(code);
    state.rules = data.rules;
    state.prog = { ...DEFAULT_PROGRAMME, ...data.prog };
    state.finance = { ...DEFAULT_FINANCE, ...(data.finance ?? {}) };
    restoreRetail(data);
    const ring = data.site.boundary as Ring;
    state.imported = { name: data.name ?? (m ? "Caz primit" : "Ultimul teren"), plot: { ring, origin: data.origin ?? [0, 0], area: Math.abs(ringArea(ring)), removed: 0, warnings: [] } };
    state.siteId = "import";
    state.site = data.site;
    buildControls(); paintSitePicker(); newPlot();
    message(m ? `Caz deschis din link: ${state.imported.name}.` : `Am redeschis ultimul teren: ${state.imported.name}. Alege alt teren din listă oricând.`);
  } catch (e) { console.error(e); if (m) message("Linkul nu conține un caz valid.", true); }
}

// ---------- generative exploration ----------

/**
 * What TestFit calls generative design, in the browser: try the programme
 * choices a developer would otherwise try one by one — wing depth, setback
 * storeys, what the ground floor is, where the cars go, balconies or not —
 * keep the best few by the KPI chosen, and apply any of them with a click.
 * The plot and the rules are never varied: those are facts, not choices.
 */
interface Trial { label: string; prog: Programme; g: Generated; score: number }
let exploring = false;
async function explore() {
  if (exploring) { exploring = false; return; }
  exploring = true;
  const btn = $("#explore");
  const baseProg = normalisedProgramme();
  const depths = [10, 12, 14, 16, 18, 20];
  const grid: { label: string; prog: Programme }[] = [];
  for (const d of depths) for (const sb of [0, 1, 2]) for (const gu of ["commercial", "residential"] as const) for (const pk of ["sub", "podium", "sol"] as const) {
    const parking = pk === "sub" ? { surface: false, podiumLevels: 0, underground: true } : pk === "podium" ? { surface: false, podiumLevels: 1, underground: true } : { surface: true, podiumLevels: 0, underground: true };
    grid.push({
      label: `tronson ${d} m · ${sb} etaje retrase · parter ${gu === "commercial" ? "comercial" : "locuințe"} · parcare ${pk === "sub" ? "subsol" : pk === "podium" ? "podium + subsol" : "la sol + subsol"}`,
      prog: { ...baseProg, depths: [d], setbackStoreys: sb, groundUse: gu, parking },
    });
  }
  const key: Record<typeof state.sortBy, (g: Generated, plot: number) => number> = {
    sun: (g) => g.layout.sunlitUsable, profit: (g, plot) => proForma(g, plot, state.finance).profit, flats: (g) => g.dwellings.total, gross: (g) => g.option.grossArea, mix: (g) => -g.mixDistance,
  };
  const trials: Trial[] = [];
  for (let i = 0; i < grid.length && exploring; i++) {
    btn.textContent = `Oprește (${i + 1} / ${grid.length})`;
    await new Promise((r) => setTimeout(r, 0)); // let the page breathe between solves
    try {
      const r = generate(state.site, state.rules, grid[i].prog, 2);
      for (const g of r.options) trials.push({ label: grid[i].label, prog: grid[i].prog, g, score: key[state.sortBy](g, r.envelope.plotArea) });
    } catch (e) { console.error(e); }
  }
  exploring = false;
  btn.textContent = "Explorează automat";
  // keep different buildings, not the same one under several programmes
  const seen = new Set<string>();
  const pool = trials.filter((t) => {
    const k = `${t.g.option.id}|${t.g.dwellings.total}|${Math.round(t.g.option.grossArea)}|${t.g.parking.capacity}`;
    return seen.has(k) ? false : (seen.add(k), true);
  });
  showExploration(pool, trials.length);
}

/**
 * The best ten of everything tried, by whichever column is clicked: the
 * ranking runs over the whole pool, so "by profit" is the ten most
 * profitable solutions, not the sunniest ten re-ordered.
 */
function showExploration(pool: Trial[], tried: number) {
  const SHOW = 10;
  const dlg = document.createElement("dialog");
  dlg.className = "compare";
  const plot = result?.envelope.plotArea ?? 1;
  const fin = new Map(pool.map((t) => [t, proForma(t.g, plot, state.finance)]));
  const storeys = (t: Trial) => t.g.option.storeys.length;
  // `low`: smaller is better, so it sorts ascending first
  const cols: { k: string; v: (t: Trial) => number; f?: (t: Trial) => string; text?: boolean; low?: boolean; name: string }[] = [
    { k: "Variantă", name: "regim de înălțime", v: (t) => storeys(t) * 10 + Object.keys(TYPOLOGY_NAME).indexOf(t.g.option.typology), f: (t) => `${TYPOLOGY_NAME[t.g.option.typology]} · P+${storeys(t) - 1}E`, text: true },
    { k: "Program", name: "adâncimea tronsonului", v: (t) => (t.prog.depths[0] ?? 0) * 10 + t.prog.setbackStoreys, f: (t) => t.label, text: true, low: true },
    { k: "Apartamente", name: "numărul de apartamente", v: (t) => t.g.dwellings.total },
    { k: "Însorite", name: "apartamentele însorite", v: (t) => t.g.layout.total - t.g.layout.sunless },
    { k: "Utilă m²", name: "suprafața utilă", v: (t) => t.g.dwellings.usable },
    { k: "CUT", name: "CUT", v: (t) => t.g.indicators.cut, f: (t) => fmt(t.g.indicators.cut, 2) },
    { k: "Parcare", name: "locurile de parcare", v: (t) => t.g.parking.capacity },
    { k: "Profit", name: "profit", v: (t) => fin.get(t)!.profit, f: (t) => money(fin.get(t)!.profit) },
    { k: "Marjă %", name: "marjă", v: (t) => fin.get(t)!.margin * 100, f: (t) => fmt(fin.get(t)!.margin * 100, 1) },
  ];
  // open on the KPI the options strip is sorted by
  let sortCol: number = ({ sun: 3, profit: 7, flats: 2, gross: 4, mix: 3 } as const)[state.sortBy];
  let desc = true;
  const paint = () => {
    const c = cols[sortCol];
    const best = [...pool].sort((a, b) => (desc ? -1 : 1) * (c.v(a) - c.v(b)) || fin.get(b)!.profit - fin.get(a)!.profit).slice(0, SHOW);
    const top = cols.map((x) => (x.text ? NaN : Math.max(...best.map(x.v))));
    dlg.innerHTML = `<div class="dhead"><b>Explorare: cele mai bune ${best.length} din ${tried} soluții, după ${c.name}${desc === !c.low ? "" : " (invers)"}</b><button class="btn" id="dclose">Închide</button></div>
      <table class="t"><tr>${cols.map((x, i) => `<th data-c="${i}" class="${i === sortCol ? "on" : ""} ${x.text ? "l" : ""}" title="Ordonează după ${x.name}">${x.k}${i === sortCol ? (desc ? " ↓" : " ↑") : ""}</th>`).join("")}<th></th></tr>
      ${best.map((t, i) => `<tr>${cols.map((x, j) => {
        const v = x.v(t), shown = x.f ? x.f(t) : fmt(v);
        if (x.text) return `<td class="${j === 1 ? "src" : ""}">${shown}</td>`;
        const cls = x.k === "Profit" ? (v >= 0 ? "pass" : "fail") : v === top[j] ? "best" : "";
        return `<td class="n ${cls}">${shown}</td>`;
      }).join("")}<td><button class="btn" data-i="${i}">Aplică</button></td></tr>`).join("")}</table>
      <div class="sub">Click pe un cap de tabel ca să alegi cele mai bune ${SHOW} după acea coloană, din toate cele ${pool.length} clădiri diferite găsite; încă un click inversează ordinea. Terenul și regulile nu se schimbă: se încearcă doar alegerile de program. „Aplică” pune programul în stânga și deschide varianta.</div>`;
    dlg.querySelector("#dclose")!.addEventListener("click", () => dlg.close());
    dlg.querySelectorAll<HTMLElement>("th[data-c]").forEach((th) => (th.onclick = () => {
      const k = +th.dataset.c!;
      desc = k === sortCol ? !desc : !cols[k].low;
      sortCol = k;
      paint();
    }));
    dlg.querySelectorAll<HTMLButtonElement>("button[data-i]").forEach((b) => (b.onclick = () => {
      const t = best[+b.dataset.i!];
      state.prog = { ...state.prog, depths: t.prog.depths, setbackStoreys: t.prog.setbackStoreys, groundUse: t.prog.groundUse, parking: { ...t.prog.parking } };
      state.optionKey = keyOf(t.g);
      dlg.close();
      buildControls();
      paintSitePicker();
      paintEdges();
      run();
    }));
  };
  paint();
  dlg.addEventListener("close", () => dlg.remove());
  document.body.appendChild(dlg);
  dlg.showModal();
}

// ---------- presentation report ----------

/** The canvas as a picture, for a given storey and view. Labels are HTML and do not come along. */
function capture(level: number, view: "3d" | "plan"): string {
  const keep = { level: state.level, view: state.view };
  const bg = scene.background, groundColour = (ground.material as THREE.MeshLambertMaterial).color.getHex();
  // a printed page is white, whatever theme the screen is in
  scene.background = new THREE.Color(0xf3f4f6);
  (ground.material as THREE.MeshLambertMaterial).color.set(0xe5e7eb);
  state.level = level; state.view = view;
  paintScene(); frameCamera(); controls.update();
  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL("image/png");
  scene.background = bg;
  (ground.material as THREE.MeshLambertMaterial).color.set(groundColour);
  state.level = keep.level; state.view = keep.view;
  paintScene(); frameCamera();
  return url;
}

/**
 * A printable report of the selected option for an investment committee or
 * a first meeting with the architect: the building, a typical floor, the
 * site, the numbers and the compliance, with what is assumed said plainly.
 */
function report() {
  if (!current || !result) return;
  const g = current, o = g.option, f = pf(g);
  // the floor on screen if one is, else the first residential one above any podium
  const typical = state.level > 0 && state.level < o.storeys.length ? state.level : Math.min(o.storeys.length - 1, Math.max(1, state.prog.parking.podiumLevels));
  const shots = { building: capture(ALL, "3d"), floor: capture(typical, "plan"), site: capture(0, "plan") };
  const name = state.imported?.name ?? sampleName(state.siteId) ?? "Teren";
  const kv = (k: string, v: string) => `<tr><td>${k}</td><td class="n">${v}</td></tr>`;
  // shown in the page, not a pop-up window: browsers block those, and print
  // keeps only this view (see the print rules for body.report-open)
  const view = document.createElement("div");
  view.id = "reportView";
  view.innerHTML = `<div class="rbar"><button class="btn primary" id="rprint">Tipărește / salvează PDF</button><button class="btn" id="rclose">Închide</button></div>
    <article class="report">${brand.logo || brand.name ? `<div class="rbrand">${brand.logo ? `<img src="${brand.logo}" alt="">` : ""}<span>${esc(brand.name)}</span></div>` : ""}<h1>${name}</h1>
    <div class="sub">${TYPOLOGY_NAME[o.typology]} · P+${o.storeys.length - 1}E · ${fmt(o.height, 1)} m · generat ${new Date().toLocaleDateString("ro-RO")} · Development Assistant, versiunea ${__COMMIT__}</div>
    <h2>Pe scurt</h2>
    <div class="kpis">
      <div>Apartamente<b>${g.dwellings.total}</b></div><div>Desfășurată<b>${fmt(o.grossArea)} m²</b></div>
      <div>CUT / POT<b>${fmt(g.indicators.cut, 2)} / ${fmt(g.indicators.pot, 1)}%</b></div><div>Profit<b class="${f.profit >= 0 ? "pass" : "fail"}">${money(f.profit)}</b></div>
    </div>
    <h2>Clădirea</h2><img src="${shots.building}">
    <div class="grid"><div><h2>${typical === 0 ? "Parter" : "Etajul " + typical}</h2><img src="${shots.floor}"></div><div><h2>Plan de situație</h2><img src="${shots.site}"></div></div>
    <h2>Apartamente</h2><table>${g.dwellings.byType.filter((b) => b.count).map((b) => kv(TYPE_NAME[b.type], `${b.count} · medie ${fmt(b.area)} m² utili`)).join("")}${kv("<b>Total</b>", `${g.dwellings.total} · ${fmt(g.dwellings.usable)} m² utili`)}${kv("Camera de zi cu ≥ 1½ h soare la 21 dec.", `${g.layout.total - g.layout.sunless} din ${g.layout.total}`)}</table>
    ${typicalFlats(g, typical)}
    <h2>Parcare</h2><table>${kv("Necesar", String(g.parking.required))}${kv("Asigurat", `${g.parking.capacity} (${[g.parking.surface && g.parking.surface + " la sol", g.parking.podium && g.parking.podium + " podium", g.parking.underground && g.parking.underground + " subsol"].filter(Boolean).join(", ")})`)}</table>
    <h2>Bilanț teritorial</h2><table>${kv("Teren", fmt(g.site.balance.plot) + " m²")}${kv("Construit la sol", fmt(g.site.balance.built) + " m²")}${kv("Circulații", fmt(g.site.balance.paved) + " m²")}${kv("Spații verzi", fmt(g.site.balance.green) + " m²")}</table>
    <h2>Financiar</h2><table>${kv("Venit", money(f.revenue.total))}${kv("Cost total", money(f.cost.total))}${kv("Profit · marjă", `${money(f.profit)} · ${fmt(f.margin * 100, 1)}%`)}</table>
    <div class="note">Valorile financiare sunt cele introduse în aplicație; cele implicite sunt orientative.</div>
    <h2>Conformitate</h2><table>${g.checks.map((c) => `<tr><td>${c.rule}${c.verified ? "" : " <span class='note'>(regulă neverificată)</span>"}<div class="note">${c.source}</div></td><td class="n ${c.verdict}">${c.value}<div class="note">${c.limit}</div></td></tr>`).join("")}</table>
    <p class="note">Studiu preliminar generat automat, nu proiect de arhitectură. Clădirile vecine nu sunt modelate; compartimentarea e schematică; regulile marcate neverificat nu au fost citite în textul oficial.</p></article>`;
  document.body.appendChild(view);
  document.body.classList.add("report-open");
  const close = () => { view.remove(); document.body.classList.remove("report-open"); };
  view.querySelector<HTMLButtonElement>("#rclose")!.onclick = close;
  view.querySelector<HTMLButtonElement>("#rprint")!.onclick = () => window.print();
}

// ---------- dragging the plot's corners ----------

const ray = new THREE.Raycaster();
const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
let dragging: { i: number; line: THREE.Line } | null = null;
function pointerAt(e: PointerEvent): THREE.Vector2 {
  const r = canvas.getBoundingClientRect();
  return new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
}
canvas.addEventListener("pointerdown", (e) => {
  ray.setFromCamera(pointerAt(e), camera);
  const hit = ray.intersectObjects(handles)[0];
  if (!hit) return;
  controls.enabled = false;
  canvas.setPointerCapture(e.pointerId);
  const line = outline(state.site.boundary, 0.4, 0xdc2626);
  world.add(line);
  dragging = { i: hit.object.userData.vertex, line };
});
canvas.addEventListener("pointermove", (e) => {
  if (!dragging) {
    ray.setFromCamera(pointerAt(e), camera);
    canvas.style.cursor = ray.intersectObjects(handles).length ? "grab" : "";
    return;
  }
  ray.setFromCamera(pointerAt(e), camera);
  const p = new THREE.Vector3();
  if (!ray.ray.intersectPlane(groundPlane, p)) return;
  // the outline follows the pointer; the engine waits for the drop
  const ring = state.site.boundary.map((q, k) => (k === dragging!.i ? [p.x, -p.z] : q)) as Ring;
  world.remove(dragging.line);
  dragging.line = outline(ring, 0.4, 0xdc2626);
  world.add(dragging.line);
  handles[dragging.i].position.set(p.x, 0.3, p.z);
});
canvas.addEventListener("pointerup", (e) => {
  if (!dragging) return;
  const i = dragging.i;
  const h = handles[i];
  dragging = null;
  controls.enabled = true;
  canvas.releasePointerCapture(e.pointerId);
  state.site.boundary[i] = [h.position.x, -h.position.z];
  if (state.imported) state.imported.plot = { ...state.imported.plot, ring: state.site.boundary, area: Math.abs(ringArea(state.site.boundary)) };
  paintEdges();
  paintPlotInfo();
  schedule();
});

// ---------- picking a flat ----------

// A click, not a drag: the orbit controls use the same button, so a press
// that moves more than a few pixels is turning the view, not choosing.
let press: { x: number; y: number } | null = null;
canvas.addEventListener("pointerdown", (e) => { press = { x: e.clientX, y: e.clientY }; });
canvas.addEventListener("pointerup", (e) => {
  if (!press || dragging || Math.hypot(e.clientX - press.x, e.clientY - press.y) > 5 || (!current && state.mode !== "retail")) { press = null; return; }
  press = null;
  ray.setFromCamera(pointerAt(e), camera);
  const id = ray.intersectObjects(pickables, true)[0]?.object.userData.unit as string | undefined;
  if (state.mode === "retail") { if (id) R.pick(id); return; }
  const u = id && current ? current.layout.floors.flatMap((f) => f.units).find((x) => x.id === id) : undefined;
  if (u) showFlat(u);
});

/** One flat as a plan: zones, walls, doors, windows and standard furniture. */
function showFlat(start: Unit) {
  if (!current) return;
  const g = current;
  let u = start;
  let furniture = stored("plan:furniture") ?? true;
  const dlg = document.createElement("dialog");
  dlg.className = "compare flatplan";
  const paint = () => {
    const floor = g.layout.floors[u.storey];
    const flats = floor.units.filter((x) => x.kind === "apartment");
    const k = flats.indexOf(u);
    const fur = furnish(u);
    const svg = flatPlanSvg(u, g.option.storeys[u.storey].footprint, fur, { furniture });
    const names = roomNames(u);
    const zones = [...new Set((u.rooms ?? []).map((r) => ZONE[r.kind]))];
    const level = u.storey === 0 ? "parter" : `etajul ${u.storey}`;
    dlg.innerHTML = `<div class="dhead"><b>Apartament ${u.id} · ${TYPE_NAME[u.type!]} · ${level}</b>
        <span class="row" style="gap:6px">
          <button class="btn" id="fprev" ${k <= 0 ? "disabled" : ""} title="Apartamentul anterior de pe etaj">←</button>
          <span class="src">${k + 1} / ${flats.length}</span>
          <button class="btn" id="fnext" ${k >= flats.length - 1 ? "disabled" : ""} title="Următorul apartament de pe etaj">→</button>
          <button class="btn" id="dclose">Închide</button>
        </span></div>
      <div class="fp">
        <div class="fpsvg">${svg}</div>
        <div class="fpside">
          <table class="t">${(u.rooms ?? []).map((r, i) => ({ r, i, name: names[i] })).sort((a, b) => a.name.localeCompare(b.name, "ro")).map(({ r, name }) => `<tr><td><span class="swatch" style="background:${ZONE_FILL[ZONE[r.kind]]}"></span>${name}</td><td class="n">${fmt(area(r.polygon), 1)} m²</td></tr>`).join("")}</table>
          <h2>Zone</h2>
          <div class="zones">${zones.map((z) => `<span><i style="background:${ZONE_FILL[z]}"></i>${ZONE_NAME[z]}</span>`).join("")}</div>
          <h2>Reguli de compartimentare</h2>
          <table class="t">${roomChecks(u).map((c) => `<tr><td class="${c.ok ? "pass" : "fail"}" style="width:18px">${c.ok ? "✓" : "!"}</td><td>${c.text}</td></tr>`).join("")}</table>
          <h2>Pe scurt</h2>
          <table class="t">${flatSummary(u).map((x) => `<tr><td>${x.label}</td><td class="n">${x.value}</td></tr>`).join("")}</table>
          ${fur.missing.length ? `<h2>Nu încape</h2><div class="sub">${fur.missing.map((m) => `${FURNITURE_NAME[m.kind]} în ${names[m.room].toLowerCase()}`).join("; ")}. E un semnal pentru plan, nu pentru mobilier.</div>` : `<div class="sub" style="margin-top:10px">Mobilierul standard încape în toate camerele, cu spațiu de folosire în fața lui și ușile libere.</div>`}
          <label class="check" style="margin-top:12px"><input type="checkbox" id="ffur" ${furniture ? "checked" : ""}> Mobilier standard</label>
          <button class="btn primary" id="fedit" style="width:100%;margin-top:10px" title="Corectează camerele și salvează un exemplu bun">Editează planul</button>
          <div class="row" style="margin-top:8px"><button class="btn" id="fsvg">Descarcă SVG</button><button class="btn" id="fprint">Tipărește</button></div>
          <div class="sub" style="margin-top:10px">Compartimentare orientativă, studiu preliminar. Pereții interiori la 10 cm, exteriori la 30 cm; mobilierul în dimensiuni uzuale (pat 160 × 200, dulap 60 adâncime, WC cu 60 cm liber în față).</div>
        </div>
      </div>`;
    dlg.querySelector("#dclose")!.addEventListener("click", () => dlg.close());
    dlg.querySelector<HTMLButtonElement>("#fprev")!.onclick = () => { u = flats[k - 1]; paint(); };
    dlg.querySelector<HTMLButtonElement>("#fnext")!.onclick = () => { u = flats[k + 1]; paint(); };
    dlg.querySelector<HTMLInputElement>("#ffur")!.onchange = (e) => { furniture = (e.target as HTMLInputElement).checked; remember("plan:furniture", furniture); paint(); };
    const file = `apartament-${u.id}-${TYPE_NAME[u.type!].toLowerCase().replace(/\s+/g, "-")}`;
    dlg.querySelector<HTMLButtonElement>("#fsvg")!.onclick = () => download(`${file}.svg`, svg, "image/svg+xml");
    dlg.querySelector<HTMLButtonElement>("#fedit")!.onclick = () => editFlat(dlg, u, g.option.storeys[u.storey].footprint, { name: state.imported?.name ?? sampleName(state.siteId) ?? "teren", option: g.option.id, storeyIndex: u.storey, onClose: paint });
    dlg.querySelector<HTMLButtonElement>("#fprint")!.onclick = () => printHtml(`<h1 style="font:600 18px system-ui;margin:0 0 4px">Apartament ${u.id} · ${TYPE_NAME[u.type!]} · ${level}</h1>
      <div style="font:12px system-ui;color:#555;margin-bottom:10px">${esc(state.imported?.name ?? "")} · ${TYPOLOGY_NAME[g.option.typology]} P+${g.option.storeys.length - 1}E · ${brand.name ? esc(brand.name) + " · " : ""}Development Assistant ${__COMMIT__}</div>
      ${svg.replace(/width="\d+" height="\d+"/, 'style="width:100%;height:auto;max-height:200mm"')}
      <table style="font:12px system-ui;border-collapse:collapse;margin-top:8px">${(u.rooms ?? []).map((r, i) => `<tr><td style="padding:2px 16px 2px 0">${names[i]}</td><td style="text-align:right">${fmt(area(r.polygon), 1)} m²</td></tr>`).join("")}
      ${flatSummary(u).map((x) => `<tr><td style="padding:2px 16px 2px 0;color:#555">${x.label}</td><td style="text-align:right">${x.value}</td></tr>`).join("")}</table>`);
  };
  paint();
  dlg.addEventListener("close", () => dlg.remove());
  document.body.appendChild(dlg);
  dlg.showModal();
}

/** One furnished plan per flat type, from the floor the report shows: the flat nearest its type's average size. */
function typicalFlats(g: Generated, level: number): string {
  const floors = [g.layout.floors[level], ...g.layout.floors].filter(Boolean);
  const types = [...new Set(g.layout.floors.flatMap((f) => f.units).filter((u) => u.kind === "apartment").map((u) => u.type!))].sort();
  const cards = types.map((t) => {
    const avg = g.dwellings.byType.find((b) => b.type === t)?.area ?? 0;
    const pool = floors.map((f) => f.units.filter((u) => u.kind === "apartment" && u.type === t)).find((x) => x.length) ?? [];
    const u = [...pool].sort((a, b) => Math.abs((a.usable ?? 0) - avg) - Math.abs((b.usable ?? 0) - avg))[0];
    if (!u) return "";
    const svg = flatPlanSvg(u, g.option.storeys[u.storey].footprint, furnish(u)).replace(/width="\d+" height="\d+"/, 'style="width:100%;height:auto"');
    return `<div class="flatcard"><b>${TYPE_NAME[t]}</b> · ${fmt(u.usable ?? 0, 1)} m² utili · apartamentul ${u.id}${svg}</div>`;
  }).join("");
  return cards ? `<h2>Apartamente tip</h2><div class="grid">${cards}</div><div class="sub">Compartimentare orientativă cu mobilier standard; fiecare tip e reprezentat de apartamentul cel mai apropiat de media lui.</div>` : "";
}

/** Print a piece of HTML without a pop-up (they get blocked): a hidden frame prints it. */
function printHtml(body: string) {
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;width:0;height:0;border:0;right:0;bottom:0";
  document.body.appendChild(f);
  const d = f.contentDocument!;
  d.open();
  d.write(`<!doctype html><html lang="ro"><head><meta charset="utf-8"><title>Plan</title><style>@page{size:A4;margin:12mm}body{margin:0}</style></head><body>${body}</body></html>`);
  d.close();
  setTimeout(() => { f.contentWindow!.focus(); f.contentWindow!.print(); setTimeout(() => f.remove(), 1000); }, 100);
}

// ---------- boot ----------

R.init({
  state, ALL, commit: __COMMIT__, brand, $, fmt, money, css, esc, seg, numField, schedule, world, prism, mat, outline, label, marker, segBar, centroid,
  tag: (o, id) => { o.traverse((x) => (x.userData.unit = id)); pickables.push(o); return o; },
  resetScene, paintSite: () => { const env = R.envelopeOf(); if (env) paintSite(env.plot); },
  frameCamera, capture, download, saveXlsx, printHtml, caseName,
  origin: () => state.imported?.plot.origin ?? [0, 0], imported: () => !!state.imported?.plot.origin[0],
});
R.onApplied(() => { buildControls(); paintSitePicker(); paintEdges(); run(); });

buildControls();
paintEdges();
paintViewbar();
theme();
matchMedia("(prefers-color-scheme: dark)").addEventListener("change", theme);
new ResizeObserver(resize).observe(canvas.parentElement!);
resize();
run();
frameCamera();
openFromLink();
renderer.setAnimationLoop(() => { controls.update(); renderer.render(scene, camera); placeLabels(); });
